import * as THREE from "three";
import { mat4, vec3, quat } from "gl-matrix";
import { applyModelVistaBaseColor } from "./modelvista_color.js";
import { applyModelVistaRendererShadowMode } from "./modelvista_renderer.js";
import { materialAssignments, assignMaterial, materialStateTargetSlot } from "./material_slots.js";
import { getModelVistaGlassSettings } from "./modelvista_glass.js";
import { getModelVistaClearcoatSettings, upgradeModelVistaPhysicalMaterial } from "./modelvista_clearcoat.js";
import { calculateTransformDuration, createRuntimeAnimationOptions, getModelVistaCompatibility,
    needsModelVistaMaterialReconstruction, resolveModelVistaNodePath, resolveEmbeddedImage } from "./modelvista_extension.js";
import { getNodeWorldMatrix } from "./camera_reference.js";

// Shared by the Viewer and Create. Host-specific side effects belong to state callbacks.
function refreshSharedSceneExtents(state) { state?.onSceneChanged?.(); }

const UI_VISIBILITY = {
    VISIBLE: "Visible",
    HIDDEN: "Hidden"
};
const CAMERA_PROJECTION_MODE = {
    PERSPECTIVE: "perspective",
    ORTHOGRAPHIC: "orthographic"
};
const GAMEPAD_AXIS_TRIGGER_THRESHOLD = 0.5;
function normalizeCameraProjectionMode(value) {
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized.includes("ortho")) {
            return CAMERA_PROJECTION_MODE.ORTHOGRAPHIC;
        }
        if (normalized.includes("persp")) {
            return CAMERA_PROJECTION_MODE.PERSPECTIVE;
        }
        if (/^[01]$/.test(normalized)) {
            return normalized === "1"
                ? CAMERA_PROJECTION_MODE.ORTHOGRAPHIC
                : CAMERA_PROJECTION_MODE.PERSPECTIVE;
        }
    }

    if (typeof value === "boolean") {
        return value
            ? CAMERA_PROJECTION_MODE.ORTHOGRAPHIC
            : CAMERA_PROJECTION_MODE.PERSPECTIVE;
    }

    if (typeof value === "number") {
        if (value === 1) {
            return CAMERA_PROJECTION_MODE.ORTHOGRAPHIC;
        }
        if (value === 0) {
            return CAMERA_PROJECTION_MODE.PERSPECTIVE;
        }
    }

    return null;
}

function firstDefinedValue(source, keys) {
    if (!source) {
        return undefined;
    }

    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) {
            return source[key];
        }
    }

    return undefined;
}

function firstFiniteNumber(source, keys) {
    const value = firstDefinedValue(source, keys);
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
}

function parseCameraOptionProjectionMode(option) {
    const explicitMode = firstDefinedValue(option, [
        "ProjectionMode",
        "ProjectionType",
        "CameraProjectionMode",
        "CameraMode",
        "Projection"
    ]);
    const normalizedMode = normalizeCameraProjectionMode(explicitMode);
    if (normalizedMode) {
        return normalizedMode;
    }

    if (option?.IsOrthographic !== undefined) {
        return normalizeCameraProjectionMode(!!option.IsOrthographic);
    }
    if (option?.UseOrthographic !== undefined) {
        return normalizeCameraProjectionMode(!!option.UseOrthographic);
    }
    if (option?.Orthographic !== undefined) {
        return normalizeCameraProjectionMode(!!option.Orthographic);
    }
    if (option?.Perspective !== undefined) {
        return option.Perspective
            ? CAMERA_PROJECTION_MODE.PERSPECTIVE
            : CAMERA_PROJECTION_MODE.ORTHOGRAPHIC;
    }

    return null;
}

function parseCameraOptionOrthographicSize(option) {
    return firstFiniteNumber(option, [
        "OrthographicSize",
        "OrthoSize",
        "OrthographicVerticalSize",
        "OrthographicHalfHeight",
        "OrthographicHeight",
        "OrthographicScale"
    ]);
}

function shouldApplyCameraProjectionMode(option, projectionMode) {
    const explicitApply = firstDefinedValue(option, [
        "ApplyProjectionMode",
        "ApplyProjection",
        "ApplyCameraMode"
    ]);
    return explicitApply !== undefined ? !!explicitApply : !!projectionMode;
}

function shouldApplyOrthographicSize(option, orthographicSize) {
    const explicitApply = firstDefinedValue(option, [
        "ApplyOrthographicSize",
        "ApplyOrthoSize",
        "ApplyOrthographicScale"
    ]);
    return explicitApply !== undefined ? !!explicitApply : Number.isFinite(orthographicSize);
}

function parseUiVisibility(value) {
    if (typeof value !== "string") {
        return UI_VISIBILITY.VISIBLE;
    }

    const trimmed = value.trim().toLowerCase();
    if (trimmed === "hidden") {
        return UI_VISIBILITY.HIDDEN;
    }

    return UI_VISIBILITY.VISIBLE;
}

function decodeThumbnail(base64, mimeType = "image/png") {
    if (typeof base64 !== "string" || base64.length === 0) {
        return null;
    }

    if (base64.startsWith("data:")) {
        return base64;
    }

    const mediaType =
        typeof mimeType === "string" && mimeType.trim().length > 0
            ? mimeType.trim()
            : "image/png";

    return `data:${mediaType};base64,${base64}`;
}

function syncAnimationControls(state, activeAnimationIndices) {
    if (!state?.isSelected) {
        return;
    }

    const uiApp = state.innoWorksUiModel?.app;
    if (!uiApp) {
        return;
    }

    uiApp.selectedAnimations = Array.isArray(activeAnimationIndices)
        ? [...activeAnimationIndices]
        : [];
}

async function prepareInnoWorksConfigurables(state, extensionData) {
    if (!state || !extensionData) {
        return null;
    }

    const containers = Array.isArray(extensionData.Containers)
        ? extensionData.Containers
        : [];
    const gltf = state.gltf;
    if (!gltf || !Array.isArray(gltf.nodes)) {
        return null;
    }

    const rootNodeIndex = findRootNodeIndex(
        gltf,
        extensionData.RootName,
        state?.sceneIndex ?? 0
    );
    if (rootNodeIndex === null || rootNodeIndex === undefined) {
        console.warn("InnoWorks: Unable to locate exported root in glTF scene graph.");
        return null;
    }

    const parentLookup = buildParentIndexLookup(gltf);
    await prepareInnoWorksSceneFeatures(state, extensionData, gltf, rootNodeIndex);

    if (containers.length === 0) {
        return null;
    }

    const runtime = new Map();
    const runtimeByPath = new Map();
    const runtimeByPathCounts = new Map();
    const ui = [];

    const configureEntry = (entry, container) => {
        if (!entry) {
            return null;
        }

        entry.applyOnAwake = container?.ApplyOnAwake === true;
        entry.autoPlay = normalizeAutoPlay(container?.AutoPlay);
        const stateData = getInnoWorksContainerStateData(container, entry.type);
        entry.previousInputAction = parseInnoWorksInputAction(stateData?.PreviousOptionAction);
        entry.nextInputAction = parseInnoWorksInputAction(stateData?.NextOptionAction);
        return entry;
    };

    const registerEntry = (entry, uiDescriptor) => {
        if (!entry) {
            return;
        }

        // Create can inspect a newly authored State without applying its default.
        // A normal Viewer load never supplies this authoring-only list.
        if (state.authoringDeferredStateIndices?.includes(Number(entry.id.split("|").at(-2)))) {
            entry.authoringDeferred = true;
            entry.appliedOptionIndex = null;
            const apply = entry.applyOption;
            entry.applyOption = (...args) => {
                entry.authoringDeferred = false;
                return apply(...args);
            };
        }

        const baseKey = makeRuntimePathKey(entry.containerPath, entry.configurableType);
        const instanceIndex = runtimeByPathCounts.get(baseKey) ?? 0;
        runtimeByPathCounts.set(baseKey, instanceIndex + 1);

        entry.runtimeInstanceIndex = instanceIndex;
        entry.runtimePathKey = makeRuntimePathKey(entry.containerPath, entry.configurableType, instanceIndex);
        entry.uiVisibility = entry.uiVisibility ?? UI_VISIBILITY.VISIBLE;

        runtime.set(entry.id, entry);
        runtimeByPath.set(entry.runtimePathKey, entry);
        if (instanceIndex === 0) {
            runtimeByPath.set(baseKey, entry);
        }

        if (uiDescriptor) {
            uiDescriptor.uiVisibility = entry.uiVisibility;
            if (entry.uiVisibility !== UI_VISIBILITY.HIDDEN) {
                ui.push(uiDescriptor);
            }
        }
    };

    containers.forEach((container, containerIndex) => {
        if (!container) {
            return;
        }

        const containerPath = container.Path ?? "";
        const configurableType = getContainerRuntimeType(container);

        const materialEntry = createMaterialRuntimeEntry(
            state,
            container,
            containerIndex,
            gltf,
            rootNodeIndex
        );
        if (materialEntry) {
            configureEntry(materialEntry, container);
            materialEntry.containerPath = containerPath;
            materialEntry.configurableType = configurableType;
            materialEntry.applyOption = (optionIndex, opts = {}) =>
                applyMaterialOption(state, materialEntry, optionIndex, opts);
            materialEntry.appliedOptionIndex = materialEntry.currentOption;
            const uiDescriptor = {
                id: materialEntry.id,
                name: materialEntry.name,
                type: materialEntry.type,
                options: materialEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: materialEntry.currentOption,
                thumbnail: materialEntry.thumbnail
            };
            registerEntry(materialEntry, uiDescriptor);
        }

        const shaderEntry = createShaderRuntimeEntry(state, container, containerIndex);
        if (shaderEntry) {
            configureEntry(shaderEntry, container);
            shaderEntry.containerPath = containerPath;
            shaderEntry.configurableType = configurableType;
            shaderEntry.applyOption = (optionIndex, opts = {}) =>
                applyShaderOption(state, shaderEntry, optionIndex, opts);
            shaderEntry.appliedOptionIndex = shaderEntry.currentOption;
            registerEntry(shaderEntry, {
                id: shaderEntry.id,
                name: shaderEntry.name,
                type: shaderEntry.type,
                options: shaderEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: shaderEntry.currentOption,
                thumbnail: shaderEntry.thumbnail
            });
        }

        const activationEntry = createActivationRuntimeEntry(
            state,
            container,
            containerIndex,
            gltf,
            rootNodeIndex
        );
        if (activationEntry) {
            configureEntry(activationEntry, container);
            activationEntry.containerPath = containerPath;
            activationEntry.configurableType = configurableType;
            activationEntry.applyOption = (optionIndex, opts = {}) =>
                applyActivationOption(state, activationEntry, optionIndex, opts);
            activationEntry.appliedOptionIndex = activationEntry.currentOption;
            const uiDescriptor = {
                id: activationEntry.id,
                name: activationEntry.name,
                type: activationEntry.type,
                options: activationEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: activationEntry.currentOption,
                thumbnail: activationEntry.thumbnail
            };
            registerEntry(activationEntry, uiDescriptor);
        }

        const transformEntry = createTransformRuntimeEntry(
            state,
            container,
            containerIndex,
            gltf,
            rootNodeIndex
        );
        if (transformEntry) {
            configureEntry(transformEntry, container);
            transformEntry.containerPath = containerPath;
            transformEntry.configurableType = configurableType;
            transformEntry.applyOption = (optionIndex, opts = {}) =>
                applyTransformOption(state, transformEntry, optionIndex, opts);
            transformEntry.appliedOptionIndex = transformEntry.currentOption;
            const uiDescriptor = {
                id: transformEntry.id,
                name: transformEntry.name,
                type: transformEntry.type,
                options: transformEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: transformEntry.currentOption,
                thumbnail: transformEntry.thumbnail
            };
            registerEntry(transformEntry, uiDescriptor);
        }

        const cameraEntry = createCameraRuntimeEntry(
            state,
            container,
            containerIndex,
            gltf,
            rootNodeIndex,
            parentLookup
        );
        if (cameraEntry) {
            configureEntry(cameraEntry, container);
            cameraEntry.containerPath = containerPath;
            cameraEntry.configurableType = configurableType;
            cameraEntry.applyOption = (optionIndex, opts = {}) =>
                applyCameraOption(state, cameraEntry, optionIndex, opts);
            cameraEntry.appliedOptionIndex = cameraEntry.currentOption;
            const uiDescriptor = {
                id: cameraEntry.id,
                name: cameraEntry.name,
                type: cameraEntry.type,
                options: cameraEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: cameraEntry.currentOption,
                thumbnail: cameraEntry.thumbnail
            };
            registerEntry(cameraEntry, uiDescriptor);
        }

        const lightEntry = createLightRuntimeEntry(
            state,
            container,
            containerIndex,
            gltf,
            rootNodeIndex
        );
        if (lightEntry) {
            configureEntry(lightEntry, container);
            lightEntry.containerPath = containerPath;
            lightEntry.configurableType = configurableType;
            lightEntry.applyOption = (optionIndex, opts = {}) =>
                applyLightOption(state, lightEntry, optionIndex, opts);
            lightEntry.appliedOptionIndex = lightEntry.currentOption;
            registerEntry(lightEntry, {
                id: lightEntry.id,
                name: lightEntry.name,
                type: lightEntry.type,
                options: lightEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: lightEntry.currentOption,
                thumbnail: lightEntry.thumbnail
            });
        }

        const groupEntry = createGroupRuntimeEntry(
            state,
            container,
            containerIndex
        );
        if (groupEntry) {
            configureEntry(groupEntry, container);
            groupEntry.containerPath = containerPath;
            groupEntry.configurableType = configurableType;
            groupEntry.applyOption = (optionIndex, opts = {}) =>
                applyGroupOption(state, groupEntry, optionIndex, opts);
            groupEntry.appliedOptionIndex = groupEntry.currentOption;
            const uiDescriptor = {
                id: groupEntry.id,
                name: groupEntry.name,
                type: groupEntry.type,
                options: groupEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: groupEntry.currentOption,
                thumbnail: groupEntry.thumbnail
            };
            registerEntry(groupEntry, uiDescriptor);
        }

        const animationEntry = createAnimationRuntimeEntry(
            state,
            container,
            containerIndex,
            gltf,
            rootNodeIndex
        );
        if (animationEntry) {
            configureEntry(animationEntry, container);
            animationEntry.containerPath = containerPath;
            animationEntry.configurableType = configurableType;
            animationEntry.applyOption = (optionIndex, opts = {}) =>
                applyAnimationOption(state, animationEntry, optionIndex, opts);
            animationEntry.appliedOptionIndex = animationEntry.currentOption;
            const uiDescriptor = {
                id: animationEntry.id,
                name: animationEntry.name,
                type: animationEntry.type,
                options: animationEntry.options.map((option) => ({
                    index: option.index,
                    label: option.label,
                    thumbnail: option.thumbnail
                })),
                currentOption: animationEntry.currentOption,
                thumbnail: animationEntry.thumbnail
            };
            registerEntry(animationEntry, uiDescriptor);
        }
    });

    if (runtime.size === 0) {
        return null;
    }

    state.innoWorksRuntime = runtime;
    state.innoWorksRuntimeByPath = runtimeByPath;
    applyInitialInnoWorksOptions(state, runtime, ui);

    return { ui, runtime, runtimeByPath };
}

function applyInitialInnoWorksOptions(state, runtime, ui) {
    if (!(runtime instanceof Map)) {
        return;
    }

    // Atomic defaults first; Group presets intentionally override them.
    [...runtime.values()].sort((a, b) => Number(a.type === "Group") - Number(b.type === "Group")).forEach((entry) => {
        // Camera states are applied after the model has been placed and framed.
        if (entry?.type === "Camera" || entry?.authoringDeferred) return;
        if (typeof entry?.applyOption !== "function") {
            return;
        }

        const appliedIndex = entry.applyOption(entry.currentOption, {
            forceImmediate: true,
            suppressUiUpdate: true
        });
        entry.appliedOptionIndex = appliedIndex;
    });

    if (Array.isArray(ui)) {
        ui.forEach((descriptor) => {
            const entry = runtime.get(descriptor?.id);
            if (entry && entry.currentOption !== undefined && entry.currentOption !== null) {
                descriptor.currentOption = entry.currentOption;
            }
        });
    }
}

function ensureRuntimeAnimationStates(state, clips, preparedConfigurables) {
    const options = createRuntimeAnimationOptions(clips);
    if (!state?.modelEntry || options.length === 0) {
        return preparedConfigurables;
    }

    const existingRuntime = preparedConfigurables?.runtime;
    const hasAnimationState = existingRuntime instanceof Map
        && Array.from(existingRuntime.values()).some((entry) => entry?.type === "Animation");
    if (hasAnimationState) {
        return preparedConfigurables;
    }

    const ui = Array.isArray(preparedConfigurables?.ui)
        ? preparedConfigurables.ui
        : [];
    const runtime = existingRuntime instanceof Map
        ? existingRuntime
        : new Map();
    const runtimeByPath = preparedConfigurables?.runtimeByPath instanceof Map
        ? preparedConfigurables.runtimeByPath
        : new Map();
    const entry = {
        id: "__runtime_animation_states__",
        containerPath: "",
        configurableType: "Animation",
        name: "Animation States",
        type: "Animation",
        options,
        currentOption: 0,
        appliedOptionIndex: 0,
        applyOnAwake: false,
        autoPlay: null,
        legacyClips: [],
        animationRootNodeIndex: null,
        animationRootPath: "",
        uiVisibility: UI_VISIBILITY.VISIBLE,
        runtimeInstanceIndex: 0,
        runtimePathKey: makeRuntimePathKey("", "Animation", 0),
        thumbnail: null,
        synthetic: true,
        applyOption: null
    };
    entry.applyOption = (optionIndex, applyOptions = {}) =>
        applyAnimationOption(state, entry, optionIndex, applyOptions);

    runtime.set(entry.id, entry);
    runtimeByPath.set(makeRuntimePathKey("", "Animation"), entry);
    runtimeByPath.set(entry.runtimePathKey, entry);
    ui.push({
        id: entry.id,
        name: entry.name,
        type: entry.type,
        options: options.map((option) => ({
            index: option.index,
            label: option.label,
            thumbnail: option.thumbnail
        })),
        currentOption: entry.currentOption,
        thumbnail: entry.thumbnail,
        uiVisibility: entry.uiVisibility,
        presentation: "buttons"
    });

    state.innoWorksRuntime = runtime;
    state.innoWorksRuntimeByPath = runtimeByPath;
    entry.applyOption(0, { forceImmediate: true, suppressUiUpdate: true });
    return { ui, runtime, runtimeByPath };
}

function makeRuntimePathKey(path, configurableType, instanceIndex) {
    const baseKey = `${path ?? ""}|${configurableType ?? ""}`;
    if (instanceIndex === undefined || instanceIndex === null || instanceIndex < 0) {
        return baseKey;
    }
    return `${baseKey}|${instanceIndex}`;
}

function getFirstNonEmptyString(...values) {
    for (const value of values) {
        if (typeof value !== "string") {
            continue;
        }
        const trimmed = value.trim();
        if (trimmed.length > 0) {
            return trimmed;
        }
    }
    return null;
}

function getContainerRuntimeType(container) {
    return getFirstNonEmptyString(
        container?.StateKind,
        container?.ContainerType,
        container?.ModelVistaType
    ) ?? "";
}

function getModelVistaDisplayName(data, container, fallback) {
    return getFirstNonEmptyString(
        data?.ModelVistaName,
        container?.StateKind,
        fallback
    ) ?? fallback;
}

function getInnoWorksContainerStateData(container, runtimeType) {
    const normalizedType = typeof runtimeType === "string"
        ? runtimeType.trim().toLowerCase()
        : "";

    if (normalizedType.includes("material")) {
        return container?.Material ?? null;
    }
    if (normalizedType.includes("shader")) {
        return container?.Shader ?? null;
    }
    if (normalizedType.includes("activation")) {
        return container?.Activation ?? null;
    }
    if (normalizedType.includes("transform")) {
        return container?.Transform ?? null;
    }
    if (normalizedType.includes("camera")) {
        return container?.Camera ?? null;
    }
    if (normalizedType.includes("light")) {
        return container?.Light ?? null;
    }
    if (normalizedType.includes("group")) {
        return container?.Group ?? null;
    }
    if (normalizedType.includes("animation")) {
        return container?.Animation ?? null;
    }

    return (
        container?.Material ??
        container?.Shader ??
        container?.Activation ??
        container?.Transform ??
        container?.Camera ??
        container?.Light ??
        container?.Group ??
        container?.Animation ??
        null
    );
}

function createInnoWorksOptionInputActions(option) {
    return {
        triggerInputAction: parseInnoWorksInputAction(option?.TriggerAction),
        holdInputAction: parseInnoWorksInputAction(option?.HoldAction)
    };
}

function parseInnoWorksInputAction(actionData) {
    const rawBindings = Array.isArray(actionData?.Bindings)
        ? actionData.Bindings
        : Array.isArray(actionData?.bindings)
            ? actionData.bindings
            : null;

    if (!actionData || !rawBindings) {
        return null;
    }

    const bindings = rawBindings
        .map(parseInnoWorksInputBinding)
        .filter(Boolean);

    if (bindings.length === 0) {
        return null;
    }

    return {
        name: typeof (actionData.Name ?? actionData.name) === "string"
            ? actionData.Name ?? actionData.name
            : "",
        type: typeof (actionData.Type ?? actionData.type) === "string"
            ? actionData.Type ?? actionData.type
            : "",
        expectedControlType:
            typeof (actionData.ExpectedControlType ?? actionData.expectedControlType) === "string"
                ? actionData.ExpectedControlType ?? actionData.expectedControlType
                : "",
        bindings
    };
}

function parseInnoWorksInputBinding(binding) {
    if (!binding || binding.IsComposite === true || binding.isComposite === true) {
        return null;
    }

    const path = typeof (binding.Path ?? binding.path) === "string"
        ? (binding.Path ?? binding.path).trim()
        : "";
    if (path.length === 0) {
        return null;
    }

    const controlPath = parseUnityInputControlPath(path);
    if (!controlPath) {
        return null;
    }

    const device = controlPath.device.toLowerCase();
    if (device.includes("keyboard")) {
        return createKeyboardInputBinding(path, controlPath.control);
    }
    if (device.includes("gamepad")) {
        return createGamepadInputBinding(path, controlPath.control);
    }

    return null;
}

function parseUnityInputControlPath(path) {
    if (typeof path !== "string") {
        return null;
    }

    const match = /^<([^>]+)>\/(.+)$/u.exec(path.trim());
    if (!match) {
        return null;
    }

    return {
        device: match[1].trim(),
        control: match[2]
            .trim()
            .replace(/\\/gu, "/")
            .replace(/^\{[^}]+\}\//u, "")
    };
}

function createKeyboardInputBinding(path, control) {
    const normalizedControl = normalizeKeyboardInputControl(control);
    if (!normalizedControl) {
        return null;
    }

    const controlKeys = getKeyboardControlKeys(normalizedControl);
    if (controlKeys.length === 0) {
        return null;
    }

    return {
        type: "keyboard",
        path,
        controlKeys
    };
}

function normalizeKeyboardInputControl(control) {
    if (typeof control !== "string") {
        return null;
    }

    const normalized = control.trim().toLowerCase().replace(/[\s_-]/gu, "");
    if (normalized.length === 0) {
        return null;
    }

    const namedControls = {
        space: { code: "Space", key: " " },
        enter: { code: "Enter", key: "enter" },
        return: { code: "Enter", key: "enter" },
        escape: { code: "Escape", key: "escape" },
        esc: { code: "Escape", key: "escape" },
        tab: { code: "Tab", key: "tab" },
        backspace: { code: "Backspace", key: "backspace" },
        delete: { code: "Delete", key: "delete" },
        insert: { code: "Insert", key: "insert" },
        home: { code: "Home", key: "home" },
        end: { code: "End", key: "end" },
        pageup: { code: "PageUp", key: "pageup" },
        pagedown: { code: "PageDown", key: "pagedown" },
        leftarrow: { code: "ArrowLeft", key: "arrowleft" },
        rightarrow: { code: "ArrowRight", key: "arrowright" },
        uparrow: { code: "ArrowUp", key: "arrowup" },
        downarrow: { code: "ArrowDown", key: "arrowdown" },
        leftshift: { code: "ShiftLeft", key: "shift" },
        rightshift: { code: "ShiftRight", key: "shift" },
        shift: { key: "shift" },
        leftctrl: { code: "ControlLeft", key: "control" },
        rightctrl: { code: "ControlRight", key: "control" },
        ctrl: { key: "control" },
        control: { key: "control" },
        leftalt: { code: "AltLeft", key: "alt" },
        rightalt: { code: "AltRight", key: "alt" },
        alt: { key: "alt" },
        option: { key: "alt" },
        leftcommand: { code: "MetaLeft", key: "meta" },
        rightcommand: { code: "MetaRight", key: "meta" },
        command: { key: "meta" },
        cmd: { key: "meta" },
        meta: { key: "meta" },
        leftmeta: { code: "MetaLeft", key: "meta" },
        rightmeta: { code: "MetaRight", key: "meta" },
        capslock: { code: "CapsLock", key: "capslock" },
        printscreen: { code: "PrintScreen", key: "printscreen" },
        scrolllock: { code: "ScrollLock", key: "scrolllock" },
        pause: { code: "Pause", key: "pause" },
        minus: { code: "Minus", key: "-" },
        dash: { code: "Minus", key: "-" },
        equal: { code: "Equal", key: "=" },
        equals: { code: "Equal", key: "=" },
        leftbracket: { code: "BracketLeft", key: "[" },
        rightbracket: { code: "BracketRight", key: "]" },
        backslash: { code: "Backslash", key: "\\" },
        semicolon: { code: "Semicolon", key: ";" },
        quote: { code: "Quote", key: "'" },
        apostrophe: { code: "Quote", key: "'" },
        backquote: { code: "Backquote", key: "`" },
        comma: { code: "Comma", key: "," },
        period: { code: "Period", key: "." },
        slash: { code: "Slash", key: "/" },
        numpadenter: { code: "NumpadEnter", key: "enter" },
        numpadplus: { code: "NumpadAdd", key: "+" },
        numpadadd: { code: "NumpadAdd", key: "+" },
        numpadminus: { code: "NumpadSubtract", key: "-" },
        numpadsubtract: { code: "NumpadSubtract", key: "-" },
        numpadmultiply: { code: "NumpadMultiply", key: "*" },
        numpaddivide: { code: "NumpadDivide", key: "/" },
        numpaddecimal: { code: "NumpadDecimal", key: "." },
        numpadperiod: { code: "NumpadDecimal", key: "." }
    };

    if (namedControls[normalized]) {
        return namedControls[normalized];
    }

    if (/^[a-z]$/u.test(normalized)) {
        return { code: `Key${normalized.toUpperCase()}`, key: normalized };
    }

    if (/^[0-9]$/u.test(normalized)) {
        return { code: `Digit${normalized}`, key: normalized };
    }

    const digitMatch = /^digit([0-9])$/u.exec(normalized);
    if (digitMatch) {
        return { code: `Digit${digitMatch[1]}`, key: digitMatch[1] };
    }

    const numpadMatch = /^numpad([0-9])$/u.exec(normalized);
    if (numpadMatch) {
        return { code: `Numpad${numpadMatch[1]}`, key: numpadMatch[1] };
    }

    const functionKeyMatch = /^f([1-9]|1[0-9]|2[0-4])$/u.exec(normalized);
    if (functionKeyMatch) {
        const key = `f${functionKeyMatch[1]}`;
        return { code: key.toUpperCase(), key };
    }

    return null;
}

function getKeyboardControlKeys(control) {
    const keys = new Set();
    if (typeof control?.code === "string" && control.code.length > 0) {
        keys.add(`keyboard:code:${control.code}`);
    }
    if (typeof control?.key === "string" && control.key.length > 0) {
        keys.add(`keyboard:key:${control.key}`);
    }
    return [...keys];
}

function createGamepadInputBinding(path, control) {
    const normalizedControl = normalizeGamepadInputControl(control);
    if (!normalizedControl) {
        return null;
    }

    return {
        type: normalizedControl.type,
        path,
        controlKeys: normalizedControl.controlKeys
    };
}

function normalizeGamepadInputControl(control) {
    if (typeof control !== "string") {
        return null;
    }

    const normalized = control.trim().toLowerCase().replace(/\\/gu, "/");
    const compact = normalized.replace(/[\s_-]/gu, "");
    const buttonIndex = getGamepadButtonIndex(normalized, compact);
    if (Number.isInteger(buttonIndex)) {
        return {
            type: "gamepad-button",
            controlKeys: [`gamepad:button:${buttonIndex}`]
        };
    }

    const axis = getGamepadAxisControl(normalized, compact);
    if (axis) {
        return {
            type: "gamepad-axis",
            controlKeys: [`gamepad:axis:${axis.axisIndex}:${axis.direction}`]
        };
    }

    return null;
}

function getGamepadButtonIndex(normalized, compact) {
    const buttonMap = new Map([
        ["buttonsouth", 0],
        ["a", 0],
        ["cross", 0],
        ["buttoneast", 1],
        ["b", 1],
        ["circle", 1],
        ["buttonwest", 2],
        ["x", 2],
        ["square", 2],
        ["buttonnorth", 3],
        ["y", 3],
        ["triangle", 3],
        ["leftshoulder", 4],
        ["leftbumper", 4],
        ["rightshoulder", 5],
        ["rightbumper", 5],
        ["lefttrigger", 6],
        ["righttrigger", 7],
        ["select", 8],
        ["back", 8],
        ["start", 9],
        ["leftstickpress", 10],
        ["leftstickbutton", 10],
        ["rightstickpress", 11],
        ["rightstickbutton", 11],
        ["dpad/up", 12],
        ["dpadup", 12],
        ["dpad/down", 13],
        ["dpaddown", 13],
        ["dpad/left", 14],
        ["dpadleft", 14],
        ["dpad/right", 15],
        ["dpadright", 15],
        ["home", 16],
        ["system", 16],
        ["guide", 16]
    ]);

    return buttonMap.get(normalized) ?? buttonMap.get(compact) ?? null;
}

function getGamepadAxisControl(normalized, compact) {
    const axisMap = new Map([
        ["leftstick/x", { axisIndex: 0, direction: "any" }],
        ["leftstickx", { axisIndex: 0, direction: "any" }],
        ["leftstick/left", { axisIndex: 0, direction: "negative" }],
        ["leftstickleft", { axisIndex: 0, direction: "negative" }],
        ["leftstick/right", { axisIndex: 0, direction: "positive" }],
        ["leftstickright", { axisIndex: 0, direction: "positive" }],
        ["leftstick/y", { axisIndex: 1, direction: "any" }],
        ["leftsticky", { axisIndex: 1, direction: "any" }],
        ["leftstick/up", { axisIndex: 1, direction: "negative" }],
        ["leftstickup", { axisIndex: 1, direction: "negative" }],
        ["leftstick/down", { axisIndex: 1, direction: "positive" }],
        ["leftstickdown", { axisIndex: 1, direction: "positive" }],
        ["rightstick/x", { axisIndex: 2, direction: "any" }],
        ["rightstickx", { axisIndex: 2, direction: "any" }],
        ["rightstick/left", { axisIndex: 2, direction: "negative" }],
        ["rightstickleft", { axisIndex: 2, direction: "negative" }],
        ["rightstick/right", { axisIndex: 2, direction: "positive" }],
        ["rightstickright", { axisIndex: 2, direction: "positive" }],
        ["rightstick/y", { axisIndex: 3, direction: "any" }],
        ["rightsticky", { axisIndex: 3, direction: "any" }],
        ["rightstick/up", { axisIndex: 3, direction: "negative" }],
        ["rightstickup", { axisIndex: 3, direction: "negative" }],
        ["rightstick/down", { axisIndex: 3, direction: "positive" }],
        ["rightstickdown", { axisIndex: 3, direction: "positive" }]
    ]);

    return axisMap.get(normalized) ?? axisMap.get(compact) ?? null;
}

function buildNodeIndexToObjectMap(gltf, sceneIndex) {
    const lookup = new Map();
    const json = gltf?.parser?.json;
    const sceneObject = gltf?.scene ?? null;
    if (!json || !Array.isArray(json.nodes) || !sceneObject) {
        return lookup;
    }

    const jsonSceneIndex = Number.isInteger(sceneIndex)
        ? sceneIndex
        : Number.isInteger(json.scene)
            ? json.scene
            : 0;
    const sceneDef = Array.isArray(json.scenes) ? json.scenes[jsonSceneIndex] : null;

    if (sceneDef && Array.isArray(sceneDef.nodes)) {
        sceneDef.nodes.forEach((nodeIndex, childIndex) => {
            mapGltfNodeObject(json, lookup, nodeIndex, sceneObject.children?.[childIndex] ?? null);
        });
    }

    // Cloned mesh nodes can share loader association objects, so hierarchy wins.
    if (gltf?.parser?.associations && typeof sceneObject.traverse === "function") {
        sceneObject.traverse((object) => {
            const association = gltf.parser.associations.get(object);
            if (
                association &&
                Number.isInteger(association.nodes) &&
                !lookup.has(association.nodes)
            ) {
                lookup.set(association.nodes, object);
            }
        });
    }

    return lookup;
}

function mapGltfNodeObject(json, lookup, nodeIndex, object) {
    if (
        !json ||
        !Array.isArray(json.nodes) ||
        !lookup ||
        !Number.isInteger(nodeIndex) ||
        nodeIndex < 0 ||
        nodeIndex >= json.nodes.length ||
        !object
    ) {
        return;
    }

    lookup.set(nodeIndex, object);

    const nodeDef = json.nodes[nodeIndex];
    const childNodeIndices = Array.isArray(nodeDef?.children) ? nodeDef.children : [];
    if (childNodeIndices.length === 0) {
        return;
    }

    const objectChildren = Array.isArray(object.children) ? object.children : [];
    const childOffset = Math.max(0, objectChildren.length - childNodeIndices.length);
    childNodeIndices.forEach((childNodeIndex, index) => {
        mapGltfNodeObject(
            json,
            lookup,
            childNodeIndex,
            objectChildren[childOffset + index] ?? null
        );
    });
}

function normalizeAutoPlay(autoPlay) {
    if (!autoPlay || autoPlay.Enable !== true) {
        return null;
    }

    const interval = Number.isFinite(autoPlay.Interval)
        ? Math.max(0, autoPlay.Interval)
        : 1;

    return {
        enabled: true,
        random: autoPlay.RandomPlayback === true,
        interval,
        elapsed: 0
    };
}

async function prepareInnoWorksSceneFeatures(state, extensionData, gltf, rootNodeIndex) {
    const compatibility = getModelVistaCompatibility(state?.innoWorksPackage);
    state.modelVistaCompatibility = compatibility;
    if (compatibility.schemaVersion > 2) {
        console.warn(
            `Model Vista: Schema ${compatibility.schemaVersion} is newer than this viewer's tested schema (2).`
        );
    }

    await buildInnoWorksEmbeddedImageLookup(state, extensionData);
    await buildInnoWorksMaterialLookup(state, extensionData, gltf, rootNodeIndex);
    // Combined assets may contain both schema-v1 canonical glTF bindings and
    // schema-v2 extension-only bindings; keep each source's loading behavior.
    applyInnoWorksMaterialBindings(state, extensionData, gltf, rootNodeIndex);
    applyInnoWorksGameObjectVisibilities(state, extensionData, gltf, rootNodeIndex);
    applyInnoWorksRendererSettings(state, extensionData, gltf, rootNodeIndex);
    applyInnoWorksMetadata(state, extensionData, gltf, rootNodeIndex);
    applyInnoWorksLights(state, extensionData, gltf, rootNodeIndex);
    hideInnoWorksMaterialDummies(state, extensionData, gltf, rootNodeIndex);
    state.modelVistaReflectionProbes = Array.isArray(extensionData?.ReflectionProbes)
        ? extensionData.ReflectionProbes
        : [];
    state.modelVistaAssetThumbnail = resolveInnoWorksThumbnail(
        state,
        extensionData?.AssetThumbnailImageIndex,
        extensionData?.AssetThumbnailBase64
    );
}

async function buildInnoWorksEmbeddedImageLookup(state, extensionData) {
    if (!state) {
        return;
    }

    const imagesByIndex = new Map();
    const records = Array.isArray(extensionData?.EmbeddedImages)
        ? extensionData.EmbeddedImages
        : [];

    const imageIndices = new Set();
    records.forEach((record) => {
        if (Number.isInteger(record?.ImageIndex)) {
            imageIndices.add(record.ImageIndex);
        }
    });
    (state?.innoWorksPackage?.json?.images ?? []).forEach((_image, imageIndex) => {
        imageIndices.add(imageIndex);
    });

    const objectUrls = [];
    await Promise.all(Array.from(imageIndices).map(async (imageIndex) => {
        try {
            const resolved = await resolveEmbeddedImage(
                state?.innoWorksPackage,
                extensionData,
                imageIndex
            );
            if (!resolved?.bytes) {
                return;
            }
            const url = URL.createObjectURL(new Blob([resolved.bytes], {
                type: resolved.mimeType || "application/octet-stream"
            }));
            objectUrls.push(url);
            imagesByIndex.set(imageIndex, {
                ...(resolved.record ?? {}),
                ImageIndex: imageIndex,
                MimeType: resolved.mimeType,
                Url: url
            });
        } catch (error) {
            console.warn(`Model Vista: Unable to decode embedded image ${imageIndex}.`, error);
        }
    }));

    state.innoWorksEmbeddedImagesByIndex = imagesByIndex;
    state.innoWorksObjectUrls = objectUrls;
}

async function buildInnoWorksMaterialLookup(state, extensionData, gltf, rootNodeIndex) {
    if (!state) {
        return;
    }

    const materialsById = new Map();
    const records = Array.isArray(extensionData?.Materials) ? extensionData.Materials : [];
    for (const record of records) {
        if (!Number.isInteger(record?.Id)) {
            continue;
        }

        const baseMaterial = resolveInnoWorksMaterialRecord(state, gltf, rootNodeIndex, record);
        const material = await createInnoWorksPbrMaterial(state, record, baseMaterial);
        if (material) {
            materialsById.set(record.Id, material);
            if (record.Name) {
                state.materialsByName?.set(record.Name, material);
            }
        }
    }

    state.innoWorksMaterialsById = materialsById;
    state.innoWorksMaterialRecordsById = new Map(
        records.filter((record) => Number.isInteger(record?.Id)).map((record) => [record.Id, record])
    );
}

function resolveInnoWorksThumbnail(state, imageIndex, base64) {
    if (Number.isInteger(imageIndex)) {
        const image = state?.innoWorksEmbeddedImagesByIndex?.get(imageIndex);
        const thumbnail = image?.Url ?? decodeThumbnail(image?.Base64, image?.MimeType);
        if (thumbnail) {
            return thumbnail;
        }
    }

    return decodeThumbnail(base64);
}

function getModelVistaProperty(record, names) {
    const wanted = new Set(names.map((name) => name.toLowerCase().replace(/^_/, "")));
    return (record?.Properties ?? []).find((property) =>
        wanted.has(String(property?.Name ?? "").toLowerCase().replace(/^_/, ""))
    ) ?? null;
}

function modelVistaVector(property, fallback = [0, 0, 0, 1]) {
    const value = property?.VectorValue;
    return [
        Number(value?.X ?? fallback[0]),
        Number(value?.Y ?? fallback[1]),
        Number(value?.Z ?? fallback[2]),
        Number(value?.W ?? fallback[3])
    ];
}

async function loadModelVistaTexture(state, property, colorTexture = false) {
    if (!Number.isInteger(property?.TextureImageIndex)) {
        return null;
    }
    const source = state?.innoWorksEmbeddedImagesByIndex?.get(property.TextureImageIndex)?.Url;
    if (!source) {
        return null;
    }
    try {
        const texture = await new THREE.TextureLoader().loadAsync(source);
        texture.flipY = false;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(
            Number(property?.TextureScale?.X ?? 1),
            Number(property?.TextureScale?.Y ?? 1)
        );
        texture.offset.set(
            Number(property?.TextureOffset?.X ?? 0),
            Number(property?.TextureOffset?.Y ?? 0)
        );
        if (colorTexture) {
            texture.colorSpace = THREE.SRGBColorSpace;
        }
        texture.needsUpdate = true;
        return texture;
    } catch (error) {
        console.warn(
            `Model Vista: Unable to load texture '${property?.TextureName ?? property.TextureImageIndex}'.`,
            error
        );
        return null;
    }
}

async function createInnoWorksPbrMaterial(state, record, baseMaterial) {
    const glassSettings = getModelVistaGlassSettings(record);
    const clearcoatSettings = getModelVistaClearcoatSettings(record);
    const compatibility = state?.modelVistaCompatibility
        ?? getModelVistaCompatibility(state?.innoWorksPackage);
    if (
        baseMaterial
        && !glassSettings
        && !clearcoatSettings
        && !needsModelVistaMaterialReconstruction(
            record,
            state?.gltf?.images?.length ?? 0,
            Number(record?.SourceSchemaVersion ?? compatibility.schemaVersion)
        )
    ) {
        return baseMaterial;
    }

    const transmission = getModelVistaProperty(record, ["transmissionFactor", "Transmission"]);
    const requiresPhysical = Boolean(glassSettings) || Boolean(clearcoatSettings)
        || Number(transmission?.FloatValue ?? 0) > 0;
    let material;
    if (baseMaterial?.isMeshStandardMaterial && (!requiresPhysical || baseMaterial.isMeshPhysicalMaterial)) {
        material = baseMaterial.clone();
    } else {
        material = requiresPhysical
            ? upgradeModelVistaPhysicalMaterial(baseMaterial)
            : new THREE.MeshStandardMaterial();
        if (baseMaterial?.color) {
            material.color.copy(baseMaterial.color);
        }
    }

    material.name = record?.Name || material.name || `ModelVistaMaterial_${record?.Id ?? ""}`;
    material.userData.modelVista = {
        materialId: record?.Id,
        shaderName: record?.ShaderName,
        shaderFamily: record?.ShaderFamily,
        sourceRenderPipeline: record?.SourceRenderPipeline,
        properties: Object.fromEntries(
            (record?.Properties ?? []).map((property) => [property?.Name, property])
        )
    };

    const baseColor = getModelVistaProperty(record, ["baseColorFactor", "BaseColor", "Color"]);
    applyModelVistaBaseColor(material, baseColor);
    const emissive = getModelVistaProperty(record, ["emissiveFactor", "EmissiveColor"])
        ?? getModelVistaProperty(record, ["EmissionColor"]);
    if (emissive && material.emissive) {
        const [r, g, b] = modelVistaVector(emissive, [0, 0, 0, 1]);
        material.emissive.setRGB(r, g, b);
    }
    const metallic = getModelVistaProperty(record, ["metallicFactor", "Metallic"]);
    const roughness = getModelVistaProperty(record, ["roughnessFactor", "Roughness"]);
    const smoothness = getModelVistaProperty(record, ["Smoothness"]);
    if (metallic) material.metalness = THREE.MathUtils.clamp(Number(metallic.FloatValue), 0, 1);
    if (roughness) material.roughness = THREE.MathUtils.clamp(Number(roughness.FloatValue), 0, 1);
    else if (smoothness) material.roughness = 1 - THREE.MathUtils.clamp(Number(smoothness.FloatValue), 0, 1);

    const alphaCutoff = getModelVistaProperty(record, ["alphaCutoff", "Cutoff"]);
    const alphaEnabled = getModelVistaProperty(record, ["AlphaCutoffEnable", "AlphaClip"]);
    if (alphaCutoff && (!alphaEnabled || Number(alphaEnabled.FloatValue) !== 0)) {
        material.alphaTest = Math.max(0, Number(alphaCutoff.FloatValue) || 0);
    }
    const doubleSided = (record?.ShaderKeywords ?? []).some((keyword) =>
        String(keyword).toLowerCase().includes("doublesided")
    ) || Number(getModelVistaProperty(record, ["DoubleSidedEnable"])?.FloatValue ?? 0) > 0;
    material.side = doubleSided ? THREE.DoubleSide : THREE.FrontSide;
    material.depthWrite = record?.RenderQueue == null || Number(record.RenderQueue) < 3000;

    const baseTexture = getModelVistaProperty(record, ["baseColorTexture", "BaseMap", "BaseColorMap", "MainTex"]);
    const normalTexture = getModelVistaProperty(record, ["normalTexture", "BumpMap", "NormalMap"]);
    const ormTexture = getModelVistaProperty(record, ["metallicRoughnessTexture", "MaskMap", "MetallicGlossMap"]);
    const occlusionTexture = getModelVistaProperty(record, ["occlusionTexture", "OcclusionMap"]);
    const emissiveTexture = getModelVistaProperty(record, ["emissiveTexture", "EmissionMap"]);
    material.map = await loadModelVistaTexture(state, baseTexture, true) ?? material.map;
    material.normalMap = await loadModelVistaTexture(state, normalTexture) ?? material.normalMap;
    const orm = await loadModelVistaTexture(state, ormTexture);
    if (orm) {
        material.metalnessMap = orm;
        material.roughnessMap = orm;
    }
    material.aoMap = await loadModelVistaTexture(state, occlusionTexture) ?? material.aoMap;
    material.emissiveMap = await loadModelVistaTexture(state, emissiveTexture, true) ?? material.emissiveMap;
    const normalScale = getModelVistaProperty(record, ["normalTexture_scale", "BumpScale"]);
    if (normalScale) {
        const scale = Number(normalScale.FloatValue);
        material.normalScale.set(scale, scale);
    }
    const occlusionStrength = getModelVistaProperty(record, ["occlusionTexture_strength", "OcclusionStrength"]);
    if (occlusionStrength) material.aoMapIntensity = Number(occlusionStrength.FloatValue);
    if (material.isMeshPhysicalMaterial && transmission) {
        material.transmission = THREE.MathUtils.clamp(Number(transmission.FloatValue), 0, 1);
    }
    if (glassSettings) Object.assign(material, glassSettings);
    if (clearcoatSettings) {
        Object.assign(material, clearcoatSettings);
        material.clearcoatMap = await loadModelVistaTexture(state, getModelVistaProperty(record, ["clearcoatTexture"])) ?? material.clearcoatMap;
        material.clearcoatRoughnessMap = await loadModelVistaTexture(state, getModelVistaProperty(record, ["clearcoatRoughnessTexture"])) ?? material.clearcoatRoughnessMap;
        material.clearcoatNormalMap = await loadModelVistaTexture(state, getModelVistaProperty(record, ["clearcoatNormalTexture"])) ?? material.clearcoatNormalMap;
        const coatNormalScale = getModelVistaProperty(record, ["clearcoatNormalTexture_Scale"]);
        if (coatNormalScale) material.clearcoatNormalScale.setScalar(Number(coatNormalScale.FloatValue));
    }
    material.needsUpdate = true;
    return material;
}

function resolveInnoWorksMaterialRecord(state, gltf, rootNodeIndex, record) {
    if (!record) {
        return null;
    }

    if (record.Name && state?.materialsByName?.has(record.Name)) {
        return state.materialsByName.get(record.Name);
    }

    const materialIndex = findMaterialIndex(gltf, record.Name);
    const material = resolveMaterialByIndex(state, materialIndex);
    if (material) {
        return material;
    }

    if (typeof record.DummyPath === "string" && record.DummyPath.length > 0) {
        const object = resolveObjectByPath(state, gltf, rootNodeIndex, record.DummyPath);
        const dummyMaterial = getFirstMaterialFromObject(object);
        if (dummyMaterial) {
            return dummyMaterial;
        }
    }

    return null;
}

function resolveObjectByPath(state, gltf, rootNodeIndex, path) {
    const nodeIndex = resolveNodeByPath(gltf, rootNodeIndex, path);
    if (nodeIndex === null || nodeIndex === undefined) {
        return null;
    }

    return state?.nodeIndexToObject?.get(nodeIndex) ?? null;
}

function getFirstMaterialFromObject(object) {
    if (!object) {
        return null;
    }

    let found = null;
    object.traverse((node) => {
        if (found || !node?.isMesh) {
            return;
        }

        if (Array.isArray(node.material)) {
            found = node.material.find((material) => !!material) ?? null;
        } else {
            found = node.material ?? null;
        }
    });

    return found;
}

function applyInnoWorksMaterialBindings(state, extensionData, gltf, rootNodeIndex) {
    const bindings = Array.isArray(extensionData?.MaterialBindings)
        ? extensionData.MaterialBindings
        : [];
    if (bindings.length === 0 || !(state?.innoWorksMaterialsById instanceof Map)) {
        return;
    }

    bindings.forEach((binding) => {
        if (Number(binding?.SourceSchemaVersion ?? extensionData.SchemaVersion ?? 1) < 2) return;
        if (!Array.isArray(binding?.MaterialIds) || binding.MaterialIds.length === 0) {
            return;
        }

        const object = resolveObjectByPath(
            state,
            gltf,
            rootNodeIndex,
            binding.RendererPath ?? ""
        );
        if (!object) {
            return;
        }

        object.traverse((node) => {
            if (!node?.isMesh) {
                return;
            }

            const primitiveIndex = state?.primitiveIndexByMesh?.get(node);
            if (Number.isInteger(primitiveIndex)) {
                const materialId = binding.MaterialIds[primitiveIndex];
                if (!Number.isInteger(materialId)) {
                    return;
                }

                const material = state.innoWorksMaterialsById.get(materialId);
                if (!material || node.material === material) {
                    return;
                }

                node.material = material;
                node.material.needsUpdate = true;
                return;
            }

            const currentMaterials = Array.isArray(node.material)
                ? [...node.material]
                : [node.material ?? null];
            const slotCount = Math.max(currentMaterials.length, binding.MaterialIds.length);
            if (slotCount === 0) {
                return;
            }

            const nextMaterials = currentMaterials.slice(0, slotCount);
            while (nextMaterials.length < slotCount) {
                nextMaterials.push(null);
            }

            let changed = false;
            binding.MaterialIds.forEach((materialId, slotIndex) => {
                if (!Number.isInteger(materialId)) {
                    return;
                }

                const material = state.innoWorksMaterialsById.get(materialId);
                if (!material || nextMaterials[slotIndex] === material) {
                    return;
                }

                nextMaterials[slotIndex] = material;
                changed = true;
            });

            if (!changed) {
                return;
            }

            node.material = nextMaterials.length === 1 ? nextMaterials[0] : nextMaterials;
            if (Array.isArray(node.material)) {
                node.material.forEach((material) => {
                    if (material) {
                        material.needsUpdate = true;
                    }
                });
            } else if (node.material) {
                node.material.needsUpdate = true;
            }
        });
    });
}

function hideInnoWorksMaterialDummies(state, extensionData, gltf, rootNodeIndex) {
    // GameObjectVisibilities is Unity activeSelf restore data. Runtime state
    // options handle web visibility; only material stash dummy meshes are hidden here.
    const visibilityByPath = new Map();

    if (Array.isArray(extensionData?.MaterialDummyPaths)) {
        extensionData.MaterialDummyPaths.forEach((path) => {
            if (typeof path === "string") {
                visibilityByPath.set(path, false);
            }
        });
    }

    visibilityByPath.forEach((visible, path) => {
        const indices = collectNodeIndicesForPath(gltf, rootNodeIndex, path);
        indices.forEach((nodeIndex) => {
            const object = state?.nodeIndexToObject?.get(nodeIndex) ?? null;
            if (!object) {
                return;
            }

            object.visible = visible;
        });
    });
}

function resolveModelVistaObject(state, gltf, rootNodeIndex, record, pathKey = "Path") {
    if (Number.isInteger(record?.NodeIndex)) {
        const byIndex = state?.nodeIndexToObject?.get(record.NodeIndex);
        if (byIndex) {
            return byIndex;
        }
    }
    return resolveObjectByPath(state, gltf, rootNodeIndex, record?.[pathKey] ?? "");
}

function applyInnoWorksGameObjectVisibilities(state, extensionData, gltf, rootNodeIndex) {
    (extensionData?.GameObjectVisibilities ?? []).forEach((record) => {
        const object = resolveModelVistaObject(state, gltf, rootNodeIndex, record);
        if (object) {
            object.visible = record?.ActiveSelf !== false;
        }
    });
}

function applyInnoWorksRendererSettings(state, extensionData, gltf, rootNodeIndex) {
    (extensionData?.RendererSettings ?? []).forEach((record) => {
        const object = resolveModelVistaObject(state, gltf, rootNodeIndex, record, "RendererPath");
        if (!object) {
            return;
        }
        const mode = String(record?.ShadowCastingMode ?? "On").toLowerCase();
        object.traverse((node) => {
            if (!node?.isMesh) {
                return;
            }
            applyModelVistaRendererShadowMode(node, mode);
        });
    });
}

function applyInnoWorksMetadata(state, extensionData, gltf, rootNodeIndex) {
    const metadata = Array.isArray(extensionData?.Metadata) ? extensionData.Metadata : [];
    state.modelVistaMetadata = metadata;
    metadata.forEach((record) => {
        const object = resolveModelVistaObject(state, gltf, rootNodeIndex, record);
        if (!object) {
            return;
        }
        object.userData.modelVistaMetadata = Object.fromEntries(
            (record?.Properties ?? [])
                .filter((property) => typeof property?.Name === "string")
                .map((property) => [property.Name, property?.Value ?? ""])
        );
    });
}

function colorTemperatureToColor(kelvin) {
    const temperature = THREE.MathUtils.clamp(Number(kelvin) || 6500, 1000, 40000) / 100;
    let red;
    let green;
    let blue;
    if (temperature <= 66) {
        red = 255;
        green = 99.4708025861 * Math.log(temperature) - 161.1195681661;
        blue = temperature <= 19 ? 0 : 138.5177312231 * Math.log(temperature - 10) - 305.0447927307;
    } else {
        red = 329.698727446 * Math.pow(temperature - 60, -0.1332047592);
        green = 288.1221695283 * Math.pow(temperature - 60, -0.0755148492);
        blue = 255;
    }
    return new THREE.Color(
        THREE.MathUtils.clamp(red, 0, 255) / 255,
        THREE.MathUtils.clamp(green, 0, 255) / 255,
        THREE.MathUtils.clamp(blue, 0, 255) / 255
    );
}

function findFirstLight(object) {
    let light = object?.isLight ? object : null;
    object?.traverse?.((child) => {
        if (!light && child?.isLight) {
            light = child;
        }
    });
    return light;
}

function applyInnoWorksLights(state, extensionData, gltf, rootNodeIndex) {
    (extensionData?.Lights ?? []).forEach((record) => {
        const object = resolveModelVistaObject(state, gltf, rootNodeIndex, record);
        const light = findFirstLight(object);
        const common = record?.Common;
        if (!light || !common) {
            return;
        }
        light.visible = common.Enabled !== false;
        const colorValue = modelVistaVector({ VectorValue: common.Color }, [1, 1, 1, 1]);
        light.color.setRGB(colorValue[0], colorValue[1], colorValue[2]);
        if (common.UseColorTemperature) {
            light.color.multiply(colorTemperatureToColor(common.ColorTemperature));
        }
        light.intensity = Math.max(0, Number(common.Intensity) || 0);
        if ("distance" in light) light.distance = Math.max(0, Number(common.Range) || 0);
        if (light.isSpotLight) {
            light.angle = THREE.MathUtils.degToRad(Number(common.SpotAngle) || 30) * 0.5;
            const outer = Math.max(0.001, Number(common.SpotAngle) || 30);
            const inner = THREE.MathUtils.clamp(Number(common.InnerSpotAngle) || 0, 0, outer);
            light.penumbra = 1 - inner / outer;
        }
        light.castShadow = String(common.Shadows ?? "None").toLowerCase() !== "none";
        if (light.shadow) {
            light.shadow.bias = Number(common.ShadowBias) || 0;
            light.shadow.normalBias = Number(common.ShadowNormalBias) || 0;
            light.shadow.camera.near = Math.max(0.001, Number(common.ShadowNearPlane) || 0.1);
        }
        light.userData.modelVistaLight = record;
    });
}

function createMaterialRuntimeEntry(state, container, containerIndex, gltf, rootNodeIndex) {
    const materialData = container?.Material;
    if (
        !materialData ||
        !Array.isArray(materialData.Options) ||
        materialData.Options.length === 0
    ) {
        return null;
    }

    const rendererBindings = [];
    const targetRenderers =
        Array.isArray(materialData.TargetRenderers) && materialData.TargetRenderers.length > 0
            ? materialData.TargetRenderers
            : [{ Path: container?.Path ?? "" }];

    targetRenderers.forEach((target) => {
        const nodeIndex = resolveNodeByPath(
            gltf,
            rootNodeIndex,
            target?.Path ?? ""
        );
        if (nodeIndex === null || nodeIndex === undefined) {
            console.warn(
                `InnoWorks: Unable to resolve renderer path '${target?.Path ?? "<null>"}'.`
            );
            return;
        }

        const binding = createRendererBinding(state, gltf, nodeIndex);
        if (!binding) {
            console.warn(
                `InnoWorks: Node '${target?.Path ?? "<null>"}' does not reference a mesh.`
            );
            return;
        }

        binding.assignments = materialAssignments(binding.object, materialStateTargetSlot(target), state.primitiveIndexByMesh);
        rendererBindings.push(binding);
    });

    if (rendererBindings.length === 0) {
        return null;
    }

    const optionEntries = [];
    materialData.Options.forEach((option, optionIndex) => {
        const materialResolution = resolveInnoWorksMaterialOption(state, gltf, option);
        if (!materialResolution) {
            console.warn(
                `InnoWorks: Material '${option?.MaterialName ?? option?.MaterialId ?? "<null>"}' not found for state '${getModelVistaDisplayName(materialData, container, container.Path ?? "<unnamed>")}'.`
            );
            return;
        }

        optionEntries.push({
            index: optionIndex,
            label:
                option?.DisplayName && option.DisplayName.trim().length > 0
                    ? option.DisplayName
                    : option?.MaterialName && option.MaterialName.trim().length > 0
                        ? option.MaterialName
                        : `Option ${optionIndex}`,
            materialIndex: materialResolution.materialIndex,
            materialId: materialResolution.materialId,
            material: materialResolution.material,
            transitionDuration: Math.max(0, option?.TransitionDuration ?? 0),
            easeMode: option?.EaseMode ?? "Linear",
            animationMode: option?.AnimationMode ?? "Time",
            ...createInnoWorksOptionInputActions(option),
            thumbnail: resolveInnoWorksThumbnail(
                state,
                option?.ThumbnailImageIndex,
                option?.ThumbnailBase64
            )
        });
    });

    if (optionEntries.length === 0) {
        return null;
    }

    const containerId = `${container.Path || ""}|${containerIndex}|material`;
    const displayName = getModelVistaDisplayName(
        materialData,
        container,
        `Material States ${containerIndex + 1}`
    );
    const initialOption = selectInitialOption(
        materialData.CurrentOption,
        optionEntries
    );

    const visibility = parseUiVisibility(materialData.UiVisibility);

    const runtimeEntry = {
        id: containerId,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: displayName,
        type: "Material",
        rendererBindings,
        options: optionEntries,
        currentOption: initialOption,
        applyOption: null,
        thumbnail: decodeThumbnail(materialData.ThumbnailBase64),
        uiVisibility: visibility
    };

    return runtimeEntry;
}

function createShaderRuntimeEntry(state, container, containerIndex) {
    const shaderData = container?.Shader;
    if (!shaderData || !Array.isArray(shaderData.Options) || shaderData.Options.length === 0) {
        return null;
    }

    const targetMaterials = new Set();
    if (shaderData.Global) {
        state?.innoWorksMaterialsById?.forEach((material) => targetMaterials.add(material));
    } else {
        (shaderData.TargetMaterials ?? []).forEach((reference) => {
            let material = Number.isInteger(reference?.MaterialId)
                ? state?.innoWorksMaterialsById?.get(reference.MaterialId)
                : null;
            material = material ?? state?.materialsByName?.get(reference?.MaterialName);
            if (material) {
                targetMaterials.add(material);
            }
        });
    }
    if (targetMaterials.size === 0) {
        return null;
    }

    const propertyType = String(shaderData.PropertyType ?? "Float");
    const options = shaderData.Options.map((option, optionIndex) => {
        let value = Number(option?.FloatValue) || 0;
        if (propertyType.toLowerCase().includes("bool")) {
            value = !!option?.BoolValue;
        } else if (propertyType.toLowerCase().includes("color")) {
            value = modelVistaVector({ VectorValue: option?.ColorValue }, [1, 1, 1, 1]);
        } else if (propertyType.toLowerCase().includes("vector")) {
            value = modelVistaVector({ VectorValue: option?.VectorValue }, [0, 0, 0, 0]);
        }
        return {
            index: optionIndex,
            label: option?.DisplayName?.trim?.() || `Option ${optionIndex}`,
            value,
            transitionDuration: Math.max(0, Number(option?.TransitionDuration) || 0),
            easeMode: option?.EaseMode ?? "Linear",
            ...createInnoWorksOptionInputActions(option),
            thumbnail: resolveInnoWorksThumbnail(
                state,
                option?.ThumbnailImageIndex,
                option?.ThumbnailBase64
            )
        };
    });

    return {
        id: `${container.Path || ""}|${containerIndex}|shader`,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: getModelVistaDisplayName(shaderData, container, `Shader States ${containerIndex + 1}`),
        type: "Shader",
        keyword: shaderData.Keyword ?? "",
        propertyType,
        targetMaterials: Array.from(targetMaterials),
        options,
        currentOption: selectInitialOption(shaderData.CurrentOption, options),
        applyOption: null,
        thumbnail: decodeThumbnail(shaderData.ThumbnailBase64),
        uiVisibility: parseUiVisibility(shaderData.UiVisibility)
    };
}

function createLightRuntimeEntry(state, container, containerIndex, gltf, rootNodeIndex) {
    const lightData = container?.Light;
    if (!lightData || !Array.isArray(lightData.Options) || lightData.Options.length === 0) {
        return null;
    }
    const targetLights = [];
    (lightData.TargetLightPaths ?? []).forEach((path) => {
        const light = findFirstLight(resolveObjectByPath(state, gltf, rootNodeIndex, path));
        if (light && !targetLights.includes(light)) {
            targetLights.push(light);
        }
    });
    if (targetLights.length === 0) {
        return null;
    }
    const options = lightData.Options.map((option, optionIndex) => ({
        index: optionIndex,
        label: option?.DisplayName?.trim?.() || `Option ${optionIndex}`,
        ev100: Number(option?.Ev100) || 0,
        temperature: Number(option?.Temperature) || 6570,
        filter: modelVistaVector({ VectorValue: option?.Filter }, [1, 1, 1, 1]),
        transitionDuration: Math.max(0, Number(option?.TransitionDuration) || 0),
        easeMode: option?.EaseMode ?? "Linear",
        ...createInnoWorksOptionInputActions(option),
        thumbnail: resolveInnoWorksThumbnail(
            state,
            option?.ThumbnailImageIndex,
            option?.ThumbnailBase64
        )
    }));
    return {
        id: `${container.Path || ""}|${containerIndex}|light`,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: getModelVistaDisplayName(lightData, container, `Light States ${containerIndex + 1}`),
        type: "Light",
        targetLights,
        options,
        currentOption: selectInitialOption(lightData.CurrentOption, options),
        applyOption: null,
        thumbnail: decodeThumbnail(lightData.ThumbnailBase64),
        uiVisibility: parseUiVisibility(lightData.UiVisibility)
    };
}

function createAnimationRuntimeEntry(state, container, containerIndex, gltf, rootNodeIndex) {
    const animationData = container?.Animation;
    if (
        !animationData ||
        !Array.isArray(animationData.Options) ||
        animationData.Options.length === 0
    ) {
        return null;
    }

    const optionEntries = animationData.Options.map((option, optionIndex) => ({
        index: optionIndex,
        label:
            option?.DisplayName && option.DisplayName.trim().length > 0
                ? option.DisplayName
                : `Option ${optionIndex}`,
        animationIndex: typeof option?.AnimationIndex === "number" ? option.AnimationIndex : -1,
        speed: typeof option?.Speed === "number" ? option.Speed : 1,
        loop: option?.Loop !== undefined ? !!option.Loop : true,
        crossFadeDuration:
            typeof option?.CrossFadeDuration === "number" ? option.CrossFadeDuration : 0.25,
        transitionDuration:
            typeof option?.TransitionDuration === "number" ? option.TransitionDuration : 0.5,
        ...createInnoWorksOptionInputActions(option),
        thumbnail: resolveInnoWorksThumbnail(
            state,
            option?.ThumbnailImageIndex,
            option?.ThumbnailBase64
        )
    }));

    if (optionEntries.length === 0) {
        return null;
    }

    const containerId = `${container.Path || ""}|${containerIndex}|animation`;
    const displayName = getModelVistaDisplayName(
        animationData,
        container,
        `Animation States ${containerIndex + 1}`
    );
    const initialOption = selectInitialOption(animationData.CurrentOption, optionEntries);
    const visibility = parseUiVisibility(animationData.UiVisibility);

    const animationRootPath =
        (typeof animationData.TargetAnimationPath === "string" && animationData.TargetAnimationPath.length > 0)
            ? animationData.TargetAnimationPath
            : container?.Path ?? "";

    const legacyClips = buildLegacyAnimationClips(
        gltf,
        rootNodeIndex,
        animationRootPath,
        animationData.LegacyClips
    );

    const resolvedAnimationRootNodeIndexRaw = resolveNodeByPath(
        gltf,
        rootNodeIndex,
        animationRootPath
    );
    let animationRootNodeIndex = resolvedAnimationRootNodeIndexRaw;
    if (animationRootNodeIndex === null || animationRootNodeIndex === undefined) {
        console.warn(
            `InnoWorks: Animation target path '${animationRootPath}' could not be resolved. Defaulting to exported root.`
        );
        animationRootNodeIndex = rootNodeIndex;
    }

    return {
        id: containerId,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: displayName,
        type: "Animation",
        options: optionEntries,
        currentOption: initialOption,
        thumbnail: decodeThumbnail(animationData.ThumbnailBase64),
        applyOption: null,
        legacyClips,
        animationRootNodeIndex,
        animationRootPath,
        uiVisibility: visibility
    };
}

function createActivationRuntimeEntry(state, container, containerIndex, gltf, rootNodeIndex) {
    const activationData = container?.Activation;
    if (
        !activationData ||
        !Array.isArray(activationData.Options) ||
        activationData.Options.length === 0
    ) {
        return null;
    }

    const optionNodeSets = [];
    const optionEntries = activationData.Options.map((option, optionIndex) => {
        const label =
            option?.DisplayName && option.DisplayName.trim().length > 0
                ? option.DisplayName
                : `Option ${optionIndex}`;

        const nodeSet = new Set();
        if (Array.isArray(option?.TargetPaths)) {
            option.TargetPaths.forEach((path) => {
                const indices = collectNodeIndicesForPath(gltf, rootNodeIndex, path);
                indices.forEach((index) => nodeSet.add(index));
            });
        }

        optionNodeSets.push(nodeSet);

        return {
            index: optionIndex,
            label,
            transitionDuration: Math.max(0, option?.TransitionDuration ?? 0),
            easeMode: option?.EaseMode ?? "Linear",
            ...createInnoWorksOptionInputActions(option),
            thumbnail: resolveInnoWorksThumbnail(
                state,
                option?.ThumbnailImageIndex,
                option?.ThumbnailBase64
            )
        };
    });

    if (optionEntries.length === 0) {
        return null;
    }

    const nodeStates = new Map();
    optionNodeSets.forEach((set) => {
        set.forEach((nodeIndex) => {
            if (!Number.isInteger(nodeIndex) || nodeIndex < 0 || nodeIndex >= gltf.nodes.length) {
                return;
            }
            if (nodeStates.has(nodeIndex)) {
                return;
            }
            const node = gltf.nodes[nodeIndex];
            const object = state?.nodeIndexToObject?.get(nodeIndex) ?? null;
            nodeStates.set(nodeIndex, {
                node,
                object,
                mesh: node?.mesh,
                light: node?.light,
                camera: node?.camera,
                nodeIndex
            });
        });
    });

    if (nodeStates.size === 0) {
        return null;
    }

    const containerId = `${container.Path || ""}|${containerIndex}|activation`;
    const displayName = getModelVistaDisplayName(
        activationData,
        container,
        `Activation States ${containerIndex + 1}`
    );
    const initialOption = selectInitialOption(
        activationData.CurrentOption,
        optionEntries
    );
    const visibility = parseUiVisibility(activationData.UiVisibility);

    return {
        id: containerId,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: displayName,
        type: "Activation",
        options: optionEntries,
        optionNodeSets,
        nodeStates,
        currentOption: initialOption,
        appliedOptionIndex: null,
        applyOption: null,
        thumbnail: decodeThumbnail(activationData.ThumbnailBase64),
        uiVisibility: visibility
    };
}

function createTransformRuntimeEntry(
    state,
    container,
    containerIndex,
    gltf,
    rootNodeIndex
) {
    const transformData = container?.Transform;
    if (
        !transformData ||
        !Array.isArray(transformData.Options) ||
        transformData.Options.length === 0
    ) {
        return null;
    }

    const targetPath = transformData.TargetTransformPath ?? "";
    const nodeIndex = resolveNodeByPath(gltf, rootNodeIndex, targetPath);
    if (nodeIndex === null || nodeIndex === undefined) {
        console.warn(
            `InnoWorks: Unable to resolve transform target '${targetPath}' for state '${getModelVistaDisplayName(transformData, container, container.Path ?? "<unnamed>")}'.`
        );
        return null;
    }

    const node = gltf.nodes?.[nodeIndex];
    const nodeObject = state?.nodeIndexToObject?.get(nodeIndex) ?? null;

    const defaultTranslation = nodeObject
        ? [nodeObject.position.x, nodeObject.position.y, nodeObject.position.z]
        : Array.isArray(node?.translation)
            ? [...node.translation]
            : [0, 0, 0];
    const defaultRotation = nodeObject
        ? normalizeQuaternion([
            nodeObject.quaternion.x,
            nodeObject.quaternion.y,
            nodeObject.quaternion.z,
            nodeObject.quaternion.w
        ])
        : normalizeQuaternion(Array.isArray(node?.rotation) ? [...node.rotation] : [0, 0, 0, 1]);

    const options = [];
    transformData.Options.forEach((option, optionIndex) => {
        const label =
            option?.DisplayName && option.DisplayName.trim().length > 0
                ? option.DisplayName
                : `Option ${optionIndex}`;

        const translation = option?.LocalPosition ? vectorFromData(option.LocalPosition) : null;
        const rotation = option?.LocalRotation ? quaternionFromData(option.LocalRotation) : null;

        const optionThumbnail = resolveInnoWorksThumbnail(
            state,
            option?.ThumbnailImageIndex,
            option?.ThumbnailBase64
        );

        options.push({
            index: optionIndex,
            label,
            applyPosition: !!option?.ApplyPosition,
            applyRotation: !!option?.ApplyRotation,
            translation,
            rotation,
            transitionDuration: Math.max(0, option?.TransitionDuration ?? 0),
            easeMode: option?.EaseMode ?? "Linear",
            ...createInnoWorksOptionInputActions(option),
            thumbnail: optionThumbnail
        });
    });

    if (options.length === 0) {
        return null;
    }

    const containerId = `${container.Path || ""}|${containerIndex}|transform`;
    const displayName = getModelVistaDisplayName(
        transformData,
        container,
        `Transform States ${containerIndex + 1}`
    );
    const initialOption = selectInitialOption(transformData.CurrentOption, options);
    const visibility = parseUiVisibility(transformData.UiVisibility);

    return {
        id: containerId,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: displayName,
        type: "Transform",
        nodeIndex,
        nodeObject,
        options,
        currentOption: initialOption,
        appliedOptionIndex: initialOption,
        defaultTranslation,
        defaultRotation,
        applyOption: null,
        thumbnail: decodeThumbnail(transformData.ThumbnailBase64),
        uiVisibility: visibility
    };
}

function createCameraRuntimeEntry(
    state,
    container,
    containerIndex,
    gltf,
    rootNodeIndex,
    parentLookup
) {
    const cameraData = container?.Camera;
    if (
        !cameraData ||
        !Array.isArray(cameraData.Options) ||
        cameraData.Options.length === 0
    ) {
        return null;
    }

    const referencePath =
        cameraData.ReferenceTransformPath ??
        container.Path ??
        "";

    const referenceNodeIndex = resolveNodeByPath(gltf, rootNodeIndex, referencePath);
    if (referenceNodeIndex === null || referenceNodeIndex === undefined) {
        console.warn(
            `InnoWorks: Unable to resolve camera reference '${referencePath}' for state '${getModelVistaDisplayName(cameraData, container, container.Path ?? "<unnamed>")}'.`
        );
        return null;
    }

    const options = [];
    cameraData.Options.forEach((option, optionIndex) => {
        const label =
            option?.DisplayName && option.DisplayName.trim().length > 0
                ? option.DisplayName
                : `Option ${optionIndex}`;

        const translationData = option?.LocalPosition
            ? vectorFromData(option.LocalPosition)
            : null;
        const translation = Array.isArray(translationData)
            ? translationData
            : [0, 0, 0];

        const rotationData = option?.LocalRotation
            ? quaternionFromData(option.LocalRotation)
            : null;
        const rotation = Array.isArray(rotationData)
            ? rotationData
            : [0, 0, 0, 1];

        const fieldOfViewDeg = Number.isFinite(option?.FieldOfView)
            ? option.FieldOfView
            : 60;
        const fieldOfViewRad = (fieldOfViewDeg * Math.PI) / 180;
        const projectionMode = parseCameraOptionProjectionMode(option);
        const orthographicSize = parseCameraOptionOrthographicSize(option);

        const optionThumbnail = resolveInnoWorksThumbnail(
            state,
            option?.ThumbnailImageIndex,
            option?.ThumbnailBase64
        );

        options.push({
            index: optionIndex,
            label,
            applyPosition: !!option?.ApplyPosition,
            applyRotation: !!option?.ApplyRotation,
            applyFieldOfView: option?.ApplyFieldOfView !== false,
            applyProjectionMode: shouldApplyCameraProjectionMode(option, projectionMode),
            applyOrthographicSize: shouldApplyOrthographicSize(option, orthographicSize),
            translation,
            rotation,
            fieldOfViewDegrees: fieldOfViewDeg,
            fieldOfViewRadians: fieldOfViewRad,
            projectionMode,
            orthographicSize,
            transitionDuration: Math.max(0, option?.TransitionDuration ?? 0),
            easeMode: option?.EaseMode ?? "Linear",
            ...createInnoWorksOptionInputActions(option),
            thumbnail: optionThumbnail
        });
    });

    if (options.length === 0) {
        return null;
    }

    const containerId = `${container.Path || ""}|${containerIndex}|camera`;
    const displayName = getModelVistaDisplayName(
        cameraData,
        container,
        `Camera States ${containerIndex + 1}`
    );
    const initialOption = selectInitialOption(cameraData.CurrentOption, options);
    const defaultFieldOfView =
        state.userCamera?.fov !== undefined
            ? (state.userCamera.fov * Math.PI) / 180
            : Math.PI / 3;
    const visibility = parseUiVisibility(cameraData.UiVisibility);

    return {
        id: containerId,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: displayName,
        type: "Camera",
        referenceNodeIndex,
        parentLookup,
        options,
        currentOption: initialOption,
        appliedOptionIndex: initialOption,
        defaultFieldOfView,
        applyOption: null,
        thumbnail: decodeThumbnail(cameraData.ThumbnailBase64),
        uiVisibility: visibility
    };
}

function createGroupRuntimeEntry(state, container, containerIndex) {
    const groupData = container?.Group;
    if (
        !groupData ||
        !Array.isArray(groupData.Options) ||
        groupData.Options.length === 0
    ) {
        return null;
    }

    const options = groupData.Options.map((option, optionIndex) => ({
        index: optionIndex,
        label:
            option?.DisplayName && option.DisplayName.trim().length > 0
                ? option.DisplayName
                : `Option ${optionIndex}`,
        transitionDuration: Math.max(0, option?.TransitionDuration ?? 0),
        easeMode: option?.EaseMode ?? "Linear",
        targets: Array.isArray(option?.Targets)
            ? option.Targets.map((target) => ({
                containerPath: target?.ContainerPath ?? "",
                containerType: getFirstNonEmptyString(
                    target?.StateKind,
                    target?.ContainerType
                ) ?? "",
                containerIndex: Number.isInteger(target?.ContainerIndex)
                    ? Math.max(0, target.ContainerIndex)
                    : null,
                optionIndex: Math.max(0, target?.SelectedOption ?? 0)
            }))
            : [],
        ...createInnoWorksOptionInputActions(option),
        thumbnail: resolveInnoWorksThumbnail(
            state,
            option?.ThumbnailImageIndex,
            option?.ThumbnailBase64
        )
    }));

    if (options.length === 0) {
        return null;
    }

    const containerId = `${container.Path || ""}|${containerIndex}|group`;
    const displayName = getModelVistaDisplayName(
        groupData,
        container,
        `Group States ${containerIndex + 1}`
    );
    const initialOption = selectInitialOption(groupData.CurrentOption, options);
    const visibility = parseUiVisibility(groupData.UiVisibility);

    return {
        id: containerId,
        containerPath: container.Path ?? "",
        configurableType: getContainerRuntimeType(container),
        name: displayName,
        type: "Group",
        transitionTimingMode: groupData.TransitionTimingMode ?? "ParentGroupDuration",
        options,
        currentOption: initialOption,
        appliedOptionIndex: initialOption,
        applyOption: null,
        thumbnail: decodeThumbnail(groupData.ThumbnailBase64),
        uiVisibility: visibility
    };
}

function findRootNodeIndex(gltf, rootName, sceneIndex) {
    if (!gltf || !Array.isArray(gltf.nodes)) {
        return null;
    }

    if (rootName) {
        const directMatch = gltf.nodes.findIndex((node) => node?.name === rootName);
        if (directMatch !== -1) {
            return directMatch;
        }
    }

    const scene = Array.isArray(gltf.scenes) ? gltf.scenes[sceneIndex ?? 0] : null;
    if (scene && Array.isArray(scene.nodes) && scene.nodes.length > 0) {
        if (rootName) {
            const sceneMatch = scene.nodes.find((nodeIndex) => {
                const node = gltf.nodes?.[nodeIndex];
                return node?.name?.toLowerCase?.() === rootName.toLowerCase();
            });
            if (sceneMatch !== undefined) {
                return sceneMatch;
            }
        }

        if (scene.nodes.length === 1) {
            return scene.nodes[0];
        }
        return scene.nodes[0];
    }

    return null;
}

function resolveNodeByPath(gltf, rootIndex, path) {
    return resolveModelVistaNodePath(gltf, rootIndex, path);
}

function collectNodeIndicesForPath(gltf, rootNodeIndex, path) {
    const indices = [];
    const nodeIndex = resolveNodeByPath(gltf, rootNodeIndex, path);
    if (nodeIndex === null || nodeIndex === undefined) {
        return indices;
    }

    collectSubtreeNodeIndices(gltf, nodeIndex, indices);
    return indices;
}

function collectSubtreeNodeIndices(gltf, nodeIndex, results) {
    if (!Array.isArray(results) || !gltf || !Array.isArray(gltf.nodes)) {
        return;
    }

    if (!Number.isInteger(nodeIndex) || nodeIndex < 0 || nodeIndex >= gltf.nodes.length) {
        return;
    }

    results.push(nodeIndex);

    const node = gltf.nodes[nodeIndex];
    if (!node || !Array.isArray(node.children)) {
        return;
    }

    node.children.forEach((child) => collectSubtreeNodeIndices(gltf, child, results));
}

function buildLegacyAnimationClips(gltf, rootNodeIndex, animationRootPath, legacyClipData) {
    if (!Array.isArray(legacyClipData) || legacyClipData.length === 0) {
        return [];
    }

    const clips = [];
    const basePath = typeof animationRootPath === "string" ? animationRootPath : "";

    legacyClipData.forEach((clipData, clipIndex) => {
        if (!clipData) {
            return;
        }

        const channels = [];
        let maxTime = 0;

        const curves = Array.isArray(clipData.Curves) ? clipData.Curves : [];
        curves.forEach((curveData) => {
            const processed = processLegacyCurve(gltf, rootNodeIndex, basePath, curveData);
            if (!processed) {
                return;
            }

            channels.push(processed.channel);
            if (processed.maxTime > maxTime) {
                maxTime = processed.maxTime;
            }
        });

        if (channels.length === 0) {
            return;
        }

        clips.push({
            sourceIndex: clipIndex,
            name:
                typeof clipData.Name === "string" && clipData.Name.length > 0
                    ? clipData.Name
                    : `Legacy Animation ${clipIndex}`,
            duration: Number.isFinite(maxTime) ? maxTime : 0,
            loop: !!clipData.LoopTime,
            wrapMode: normalizeLegacyWrapMode(clipData.WrapMode),
            channels,
            sampleRate: typeof clipData.SampleRate === "number" ? clipData.SampleRate : 60
        });
    });

    return clips;
}

function processLegacyCurve(gltf, rootNodeIndex, basePath, curveData) {
    if (!curveData) {
        return null;
    }

    const propertyName = typeof curveData.PropertyName === "string"
        ? curveData.PropertyName
        : "";
    if (!propertyName) {
        return null;
    }

    const mapping = mapLegacyProperty(propertyName);
    if (!mapping) {
        return null;
    }

    const combinedPath = combineAnimationPath(basePath, curveData.Path);
    const nodeIndex = resolveNodeByPath(gltf, rootNodeIndex, combinedPath);
    if (nodeIndex === null || nodeIndex === undefined) {
        return null;
    }

    const curve = createLegacyCurve(curveData);
    if (!curve) {
        return null;
    }

    return {
        channel: {
            nodeIndex,
            property: mapping.property,
            axis: mapping.axis,
            curve
        },
        maxTime: curve.endTime
    };
}

function combineAnimationPath(basePath, relativePath) {
    const base = typeof basePath === "string" ? basePath.trim() : "";
    const rel = typeof relativePath === "string" ? relativePath.trim() : "";

    if (!base) {
        return rel;
    }
    if (!rel) {
        return base;
    }

    return `${base.replace(/\/+$/u, "")}/${rel.replace(/^\/+/u, "")}`;
}

function mapLegacyProperty(propertyName) {
    if (typeof propertyName !== "string" || propertyName.length === 0) {
        return null;
    }

    const sanitized = propertyName.replace(/^m_/iu, "");
    const lower = sanitized.toLowerCase();

    const axisChar = sanitized.slice(sanitized.lastIndexOf(".") + 1).toLowerCase();
    const axis =
        axisChar === "x"
            ? 0
            : axisChar === "y"
                ? 1
                : axisChar === "z"
                    ? 2
                    : axisChar === "w"
                        ? 3
                        : null;

    if (lower.startsWith("localposition.") && axis !== null && axis <= 2) {
        return { property: "translation", axis };
    }

    if (lower.startsWith("localscale.") && axis !== null && axis <= 2) {
        return { property: "scale", axis };
    }

    if (lower.startsWith("localrotation.") && axis !== null && axis <= 3) {
        return { property: "rotation", axis };
    }

    if (lower.startsWith("localeulerangles") && axis !== null && axis <= 2) {
        return { property: "rotationEuler", axis };
    }

    return null;
}

function createLegacyCurve(curveData) {
    const keyframesSrc = Array.isArray(curveData.Keyframes) ? curveData.Keyframes : [];
    if (keyframesSrc.length === 0) {
        return null;
    }

    const keyframes = keyframesSrc
        .map((k) => ({
            time: typeof k.Time === "number" ? k.Time : 0,
            value: typeof k.Value === "number" ? k.Value : 0,
            inTangent: typeof k.InTangent === "number" ? k.InTangent : 0,
            outTangent: typeof k.OutTangent === "number" ? k.OutTangent : 0,
            inWeight: typeof k.InWeight === "number" ? k.InWeight : 0,
            outWeight: typeof k.OutWeight === "number" ? k.OutWeight : 0,
            weightedMode: typeof k.WeightedMode === "number" ? k.WeightedMode : 0
        }))
        .sort((a, b) => a.time - b.time);

    const startTime = keyframes[0].time ?? 0;
    const endTime = keyframes[keyframes.length - 1].time ?? startTime;

    return {
        keyframes,
        startTime,
        endTime,
        preWrapMode: normalizeLegacyWrapMode(curveData.PreWrapMode),
        postWrapMode: normalizeLegacyWrapMode(curveData.PostWrapMode)
    };
}

function normalizeLegacyWrapMode(value) {
    if (typeof value === "string") {
        return value;
    }

    switch (value) {
    case 2:
        return "Loop";
    case 4:
        return "PingPong";
    case 8:
        return "ClampForever";
    case 1:
        return "Once";
    default:
        return "Default";
    }
}

function createRendererBinding(state, gltf, nodeIndex) {
    const object = state?.nodeIndexToObject?.get(nodeIndex) ?? null;
    if (!object) {
        return null;
    }
    const meshes = [];
    const defaultMaterials = new Map();
    object.traverse((node) => {
        if (node.isMesh) {
            meshes.push(node);
            defaultMaterials.set(node, node.material);
        }
    });
    if (meshes.length === 0) {
        return null;
    }
    return {
        nodeIndex,
        object,
        meshes,
        defaultMaterials
    };
}

function resolveInnoWorksMaterialOption(state, gltf, option) {
    const materialId = Number.isInteger(option?.MaterialId) ? option.MaterialId : null;
    if (materialId !== null && state?.innoWorksMaterialsById?.has(materialId)) {
        return {
            materialId,
            materialIndex: null,
            material: state.innoWorksMaterialsById.get(materialId)
        };
    }

    const materialIndex = findMaterialIndex(gltf, option?.MaterialName);
    if (materialIndex !== null && materialIndex !== undefined) {
        return {
            materialId,
            materialIndex,
            material: resolveMaterialByIndex(state, materialIndex)
        };
    }

    if (option?.MaterialName && state?.materialsByName?.has(option.MaterialName)) {
        return {
            materialId,
            materialIndex: null,
            material: state.materialsByName.get(option.MaterialName)
        };
    }

    return null;
}

function findMaterialIndex(gltf, materialName) {
    if (!Array.isArray(gltf?.materials) || !materialName) {
        return null;
    }

    let index = gltf.materials.findIndex((material) => material?.name === materialName);
    if (index !== -1) {
        return index;
    }

    index = gltf.materials.findIndex(
        (material) => material?.name?.toLowerCase?.() === materialName.toLowerCase()
    );
    if (index !== -1) {
        return index;
    }

    index = gltf.materials.findIndex((material) =>
        material?.name?.startsWith?.(materialName)
    );
    if (index !== -1) {
        return index;
    }

    return null;
}

function selectInitialOption(requestedIndex, options) {
    if (!Array.isArray(options) || options.length === 0) {
        return 0;
    }

    if (requestedIndex !== undefined && requestedIndex !== null) {
        const parsed = Number(requestedIndex);
        const exact = options.find((option) => option.index === parsed);
        if (exact) {
            return exact.index;
        }
    }

    return options[0].index;
}

function shaderKeywordKey(keyword) {
    return String(keyword ?? "").toLowerCase().replace(/^_/, "");
}

function readShaderMaterialValue(material, entry) {
    const cached = material?.userData?.modelVistaShaderValues?.[entry.keyword];
    if (cached !== undefined) {
        return Array.isArray(cached) ? [...cached] : cached;
    }
    const key = shaderKeywordKey(entry.keyword);
    if (["basecolorfactor", "basecolor", "color"].includes(key) && material?.color) {
        return [material.color.r, material.color.g, material.color.b, material.opacity ?? 1];
    }
    if (["emissivefactor", "emissioncolor", "emissive"].includes(key) && material?.emissive) {
        return [material.emissive.r, material.emissive.g, material.emissive.b, 1];
    }
    if (["metallicfactor", "metallic"].includes(key)) return material?.metalness ?? 0;
    if (["roughnessfactor", "roughness"].includes(key)) return material?.roughness ?? 1;
    if (key === "smoothness") return 1 - (material?.roughness ?? 1);
    if (["alphacutoff", "cutoff"].includes(key)) return material?.alphaTest ?? 0;
    if (key === "opacity") return material?.opacity ?? 1;
    if (key.includes("doublesided")) return material?.side === THREE.DoubleSide;
    return entry.propertyType.toLowerCase().includes("vector") || entry.propertyType.toLowerCase().includes("color")
        ? [0, 0, 0, 0]
        : 0;
}

function applyShaderMaterialValue(material, entry, value) {
    if (!material) {
        return;
    }
    material.userData.modelVistaShaderValues = material.userData.modelVistaShaderValues ?? {};
    material.userData.modelVistaShaderValues[entry.keyword] = Array.isArray(value) ? [...value] : value;
    const key = shaderKeywordKey(entry.keyword);
    if (["basecolorfactor", "basecolor", "color"].includes(key) && Array.isArray(value)) {
        material.color?.setRGB(value[0], value[1], value[2]);
        material.opacity = value[3] ?? material.opacity;
        material.transparent = material.opacity < 0.999;
    } else if (["emissivefactor", "emissioncolor", "emissive"].includes(key) && Array.isArray(value)) {
        material.emissive?.setRGB(value[0], value[1], value[2]);
    } else if (["metallicfactor", "metallic"].includes(key)) {
        material.metalness = THREE.MathUtils.clamp(Number(value), 0, 1);
    } else if (["roughnessfactor", "roughness"].includes(key)) {
        material.roughness = THREE.MathUtils.clamp(Number(value), 0, 1);
    } else if (key === "smoothness") {
        material.roughness = 1 - THREE.MathUtils.clamp(Number(value), 0, 1);
    } else if (["alphacutoff", "cutoff"].includes(key)) {
        material.alphaTest = Math.max(0, Number(value) || 0);
    } else if (key === "opacity") {
        material.opacity = THREE.MathUtils.clamp(Number(value), 0, 1);
        material.transparent = material.opacity < 0.999;
    } else if (key === "transmission" && material.isMeshPhysicalMaterial) {
        material.transmission = THREE.MathUtils.clamp(Number(value), 0, 1);
    } else if (key.includes("doublesided")) {
        material.side = value ? THREE.DoubleSide : THREE.FrontSide;
    }
    material.needsUpdate = true;
}

function interpolateModelVistaValue(fromValue, toValue, t) {
    if (Array.isArray(fromValue) && Array.isArray(toValue)) {
        return toValue.map((value, index) => THREE.MathUtils.lerp(Number(fromValue[index]) || 0, Number(value) || 0, t));
    }
    if (typeof toValue === "boolean") {
        return t >= 0.5 ? toValue : !!fromValue;
    }
    return THREE.MathUtils.lerp(Number(fromValue) || 0, Number(toValue) || 0, t);
}

function applyShaderOption(state, entry, requestedIndex, options = {}) {
    const targetOption = entry?.options?.find((option) => option.index === Number(requestedIndex))
        ?? entry?.options?.[0];
    if (!targetOption) return entry?.currentOption ?? 0;
    const {
        forceImmediate = false,
        suppressUiUpdate = false,
        durationOverride = null,
        easeModeOverride = null
    } = options ?? {};
    const duration = forceImmediate ? 0 : Math.max(0, Number.isFinite(durationOverride)
        ? durationOverride
        : targetOption.transitionDuration);
    removeInnoWorksAnimation(state, entry.id, "shader");
    if (duration > 0) {
        ensureAnimationState(state);
        state.innoWorksActiveAnimations.push({
            type: "shader",
            entry,
            option: targetOption,
            duration,
            elapsed: 0,
            easeMode: easeModeOverride ?? targetOption.easeMode,
            fromValues: entry.targetMaterials.map((material) => readShaderMaterialValue(material, entry))
        });
    } else {
        entry.targetMaterials.forEach((material) => applyShaderMaterialValue(material, entry, targetOption.value));
        entry.appliedOptionIndex = targetOption.index;
    }
    entry.currentOption = targetOption.index;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return entry.currentOption;
}

function ev100ToIntensity(ev100) {
    return Math.pow(2, Number(ev100) - 3);
}

function applyLightStateValue(light, ev100, temperature, filter) {
    light.intensity = ev100ToIntensity(ev100);
    const filterColor = new THREE.Color(filter[0], filter[1], filter[2]);
    light.color.copy(filterColor).multiply(colorTemperatureToColor(temperature));
    light.userData.modelVistaStateTemperature = temperature;
    light.userData.modelVistaStateFilter = [...filter];
}

function applyLightOption(state, entry, requestedIndex, options = {}) {
    const targetOption = entry?.options?.find((option) => option.index === Number(requestedIndex))
        ?? entry?.options?.[0];
    if (!targetOption) return entry?.currentOption ?? 0;
    const {
        forceImmediate = false,
        suppressUiUpdate = false,
        durationOverride = null,
        easeModeOverride = null
    } = options ?? {};
    const duration = forceImmediate ? 0 : Math.max(0, Number.isFinite(durationOverride)
        ? durationOverride
        : targetOption.transitionDuration);
    removeInnoWorksAnimation(state, entry.id, "light");
    if (duration > 0) {
        ensureAnimationState(state);
        state.innoWorksActiveAnimations.push({
            type: "light",
            entry,
            option: targetOption,
            duration,
            elapsed: 0,
            easeMode: easeModeOverride ?? targetOption.easeMode,
            fromValues: entry.targetLights.map((light) => ({
                ev100: Math.log2(Math.max(0.0001, light.intensity)) + 3,
                temperature: Number(light.userData.modelVistaStateTemperature) || 6570,
                filter: light.userData.modelVistaStateFilter ?? [light.color.r, light.color.g, light.color.b, 1]
            }))
        });
    } else {
        entry.targetLights.forEach((light) => applyLightStateValue(
            light,
            targetOption.ev100,
            targetOption.temperature,
            targetOption.filter
        ));
        entry.appliedOptionIndex = targetOption.index;
    }
    entry.currentOption = targetOption.index;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return entry.currentOption;
}

function applyMaterialOption(state, entry, requestedIndex, options = {}) {
    if (!entry || !Array.isArray(entry.options) || entry.options.length === 0) {
        return entry?.currentOption ?? 0;
    }

    const {
        forceImmediate = false,
        suppressUiUpdate = false,
        durationOverride = null,
        easeModeOverride = null
    } = options ?? {};

    const parsedIndex = Number(requestedIndex);
    const numericIndex = Number.isNaN(parsedIndex)
        ? Number(entry.currentOption ?? entry.options[0].index)
        : parsedIndex;

    const targetOption =
        entry.options.find((option) => option.index === numericIndex) ??
        entry.options.find((option) => option.index === entry.currentOption) ??
        entry.options[0];

    if (!targetOption) {
        return entry.currentOption ?? 0;
    }

    const duration = forceImmediate
        ? 0
        : Math.max(0, Number.isFinite(durationOverride)
            ? durationOverride
            : targetOption.transitionDuration ?? 0);

    const fromIndex =
        entry.appliedOptionIndex !== undefined
            ? entry.appliedOptionIndex
            : entry.currentOption ?? targetOption.index;
    const fromOption =
        entry.options.find((option) => option.index === fromIndex) ?? targetOption;

    removeInnoWorksAnimation(state, entry.id, "material");

    if (duration > 0 && state) {
        scheduleMaterialAnimation(
            state,
            entry,
            fromOption,
            targetOption,
            duration,
            easeModeOverride ?? targetOption.easeMode
        );
    } else {
        applyMaterialOptionImmediate(state, entry, targetOption);
    }

    entry.currentOption = targetOption.index;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return entry.currentOption;
}

function applyAnimationOption(state, entry, requestedIndex, options = {}) {
    if (!entry || !Array.isArray(entry.options) || entry.options.length === 0) {
        return entry?.currentOption ?? 0;
    }

    const { suppressUiUpdate = false } = options ?? {};

    const parsedIndex = Number(requestedIndex);
    const numericIndex = Number.isNaN(parsedIndex) ? entry.currentOption ?? 0 : parsedIndex;
    const clampedIndex = Math.max(0, Math.min(entry.options.length - 1, numericIndex));

    const option = entry.options[clampedIndex];
    entry.currentOption = clampedIndex;

    if (!option) {
        entry.appliedOptionIndex = clampedIndex;
        updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
        return clampedIndex;
    }

    removeInnoWorksAnimation(state, entry.id, "legacy-animation");
    state.animationPlaybackPaused = false;

    const modelEntry = state?.modelEntry;
    const legacyClips = Array.isArray(entry.legacyClips) ? entry.legacyClips : [];
    const legacyClipIndex =
        Number.isInteger(option.animationIndex) && option.animationIndex >= 0
            ? option.animationIndex
            : clampedIndex;
    const legacyClip =
        legacyClips.find(clip => clip?.sourceIndex === legacyClipIndex) ??
        legacyClips[legacyClipIndex] ??
        legacyClips[clampedIndex] ??
        null;

    if (!modelEntry || !Array.isArray(modelEntry.actions) || option.animationIndex == null) {
        if (legacyClip && state) {
            stopModelActions(modelEntry);
            scheduleLegacyAnimation(state, entry, legacyClip, option);
        }
        entry.appliedOptionIndex = clampedIndex;
        updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
        return clampedIndex;
    }

    if (option.animationIndex < 0) {
        stopModelActions(modelEntry);
        syncAnimationControls(state, []);
        entry.appliedOptionIndex = clampedIndex;
        updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
        return clampedIndex;
    }

    if (option.animationIndex >= modelEntry.actions.length) {
        if (legacyClip && state) {
            stopModelActions(modelEntry);
            scheduleLegacyAnimation(state, entry, legacyClip, option);
        }
        entry.appliedOptionIndex = clampedIndex;
        updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
        return clampedIndex;
    }

    const previousIndices = Array.isArray(modelEntry.activeAnimationIndices)
        ? modelEntry.activeAnimationIndices.filter((index) => index !== option.animationIndex)
        : [];
    const targetAction = modelEntry.actions[option.animationIndex];
    targetAction.reset();
    targetAction.enabled = true;
    targetAction.setEffectiveWeight(1);
    targetAction.timeScale = Number.isFinite(option.speed) ? option.speed : 1;
    const loop = option.loop !== undefined ? option.loop : true;
    targetAction.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
    targetAction.clampWhenFinished = !loop;
    targetAction.play();

    const crossFadeDuration = Math.max(0, Number(option.crossFadeDuration) || 0);
    modelEntry.actions.forEach((action, index) => {
        if (index === option.animationIndex) {
            return;
        }
        if (crossFadeDuration > 0 && previousIndices.includes(index)) {
            action.crossFadeTo(targetAction, crossFadeDuration, false);
        } else {
            action.stop();
        }
    });
    modelEntry.activeAnimationIndices = [option.animationIndex];
    modelEntry.animationPlaying = true;
    syncAnimationControls(state, modelEntry.activeAnimationIndices);

    entry.appliedOptionIndex = clampedIndex;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return clampedIndex;
}

function applyActivationOption(state, entry, requestedIndex, options = {}) {
    if (!entry || !Array.isArray(entry.options) || entry.options.length === 0) {
        return entry?.currentOption ?? 0;
    }

    const { suppressUiUpdate = false } = options ?? {};

    const parsedIndex = Number(requestedIndex);
    const numericIndex = Number.isNaN(parsedIndex)
        ? Number(entry.currentOption ?? entry.options[0].index)
        : parsedIndex;

    const targetOption =
        entry.options.find((option) => option.index === numericIndex) ??
        entry.options.find((option) => option.index === entry.currentOption) ??
        entry.options[0];

    if (!targetOption) {
        return entry.currentOption ?? 0;
    }

    const toSet = entry.optionNodeSets[targetOption.index] ?? new Set();

    removeInnoWorksAnimation(state, entry.id, "activation");

    const changedState = setActivationNodes(state, entry, toSet);
    if (changedState) {
        invalidateRendererScene(state);
        refreshSharedSceneExtents(state);
    }
    entry.appliedOptionIndex = targetOption.index;

    entry.currentOption = targetOption.index;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return entry.currentOption;
}

function applyTransformOption(state, entry, requestedIndex, options = {}) {
    if (
        !entry ||
        typeof entry.nodeIndex !== "number" ||
        !Array.isArray(entry.options) ||
        entry.options.length === 0
    ) {
        return entry?.currentOption ?? 0;
    }

    const object =
        entry.nodeObject ??
        state?.nodeIndexToObject?.get(entry.nodeIndex) ??
        null;
    if (!object) {
        return entry.currentOption ?? 0;
    }

    const {
        forceImmediate = false,
        suppressUiUpdate = false,
        durationOverride = null,
        easeModeOverride = null
    } = options ?? {};

    const parsedIndex = Number(requestedIndex);
    const numericIndex = Number.isNaN(parsedIndex)
        ? Number(entry.currentOption ?? entry.options[0].index)
        : parsedIndex;

    const targetOption =
        entry.options.find((option) => option.index === numericIndex) ??
        entry.options.find((option) => option.index === entry.currentOption) ??
        entry.options[0];

    if (!targetOption) {
        return entry.currentOption ?? 0;
    }

    const targetTranslation =
        targetOption.applyPosition && Array.isArray(targetOption.translation)
            ? targetOption.translation
            : entry.defaultTranslation;
    const targetRotation =
        targetOption.applyRotation && Array.isArray(targetOption.rotation)
            ? targetOption.rotation
            : entry.defaultRotation;
    const duration = forceImmediate
        ? 0
        : Math.max(0, Number.isFinite(durationOverride)
            ? durationOverride
            : calculateTransformDuration(
                targetOption.animationMode,
                targetOption.transitionDuration,
                object.position.toArray(),
                targetTranslation,
                object.quaternion.toArray(),
                targetRotation
            ));

    removeInnoWorksAnimation(state, entry.id, "transform");

    if (duration > 0) {
        scheduleTransformAnimation(
            state,
            entry,
            targetOption,
            duration,
            easeModeOverride ?? targetOption.easeMode
        );
    } else {
        const nextTranslation = targetTranslation;
        const nextRotation = targetRotation;
        if (Array.isArray(nextTranslation)) {
            object.position.set(nextTranslation[0], nextTranslation[1], nextTranslation[2]);
        }
        if (Array.isArray(nextRotation)) {
            object.quaternion.set(
                nextRotation[0],
                nextRotation[1],
                nextRotation[2],
                nextRotation[3]
            );
        }
        object.updateMatrixWorld(true);
        refreshSharedSceneExtents(state);

        entry.appliedOptionIndex = targetOption.index;
    }

    entry.currentOption = targetOption.index;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return entry.currentOption;
}

function applyCameraOption(state, entry, requestedIndex, options = {}) {
    if (!entry || !Array.isArray(entry.options) || entry.options.length === 0) {
        return entry?.currentOption ?? 0;
    }

    const userCamera = state?.userCamera;
    if (!userCamera) {
        console.warn("InnoWorks: User camera is not available.");
        return entry.currentOption ?? 0;
    }

    const {
        forceImmediate = false,
        suppressUiUpdate = false,
        durationOverride = null,
        easeModeOverride = null
    } = options ?? {};

    const parsedIndex = Number(requestedIndex);
    const numericIndex = Number.isNaN(parsedIndex)
        ? Number(entry.currentOption ?? entry.options[0].index)
        : parsedIndex;

    const targetOption =
        entry.options.find((option) => option.index === numericIndex) ??
        entry.options.find((option) => option.index === entry.currentOption) ??
        entry.options[0];

    if (!targetOption) {
        return entry.currentOption ?? 0;
    }

    const gltf = state?.gltf;
    const referenceMatrix = getNodeWorldMatrix(
        state,
        gltf,
        entry.referenceNodeIndex,
        entry.parentLookup
    );

    const worldPose = computeCameraWorldPose(referenceMatrix, targetOption);

    const duration = forceImmediate
        ? 0
        : Math.max(0, Number.isFinite(durationOverride)
            ? durationOverride
            : targetOption.transitionDuration ?? 0);

    removeInnoWorksAnimation(state, entry.id, "camera");

    if (duration > 0) {
        scheduleCameraAnimation(
            state,
            entry,
            targetOption,
            worldPose,
            duration,
            easeModeOverride ?? targetOption.easeMode
        );
    } else {
        applyCameraPoseImmediate(state, entry, targetOption, worldPose);
        entry.appliedOptionIndex = targetOption.index;
    }

    entry.currentOption = targetOption.index;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return entry.currentOption;
}

function computeCameraWorldPose(referenceMatrix, option) {
    const translation = Array.isArray(option?.translation)
        ? option.translation
        : [0, 0, 0];
    const rotation = Array.isArray(option?.rotation)
        ? option.rotation
        : [0, 0, 0, 1];

    const localMatrix = mat4.create();
    const rotationQuat = quat.normalize(
        quat.create(),
        quat.fromValues(rotation[0], rotation[1], rotation[2], rotation[3])
    );
    const translationVec = vec3.fromValues(translation[0], translation[1], translation[2]);
    mat4.fromRotationTranslation(localMatrix, rotationQuat, translationVec);

    const worldMatrix = mat4.create();
    if (referenceMatrix) {
        mat4.multiply(worldMatrix, referenceMatrix, localMatrix);
    } else {
        mat4.copy(worldMatrix, localMatrix);
    }

    const adjustmentMatrix = mat4.create();
    mat4.fromRotation(adjustmentMatrix, Math.PI, vec3.fromValues(0, 1, 0));
    mat4.multiply(worldMatrix, worldMatrix, adjustmentMatrix);

    const worldPositionVec = vec3.create();
    mat4.getTranslation(worldPositionVec, worldMatrix);
    const worldRotationQuat = quat.create();
    mat4.getRotation(worldRotationQuat, worldMatrix);

    return {
        position: [worldPositionVec[0], worldPositionVec[1], worldPositionVec[2]],
        rotation: [
            worldRotationQuat[0],
            worldRotationQuat[1],
            worldRotationQuat[2],
            worldRotationQuat[3]
        ],
        fieldOfView: option.applyFieldOfView ? option.fieldOfViewRadians : null,
        projectionMode: option.applyProjectionMode ? option.projectionMode : null,
        orthographicSize: option.applyOrthographicSize ? option.orthographicSize : null
    };
}

function applyCameraProjectionFromOption(state, option, worldPose, options = {}) {
    const { applyScalars = true } = options ?? {};
    const projection = {};

    if (option?.applyProjectionMode && worldPose?.projectionMode) {
        projection.projectionMode = worldPose.projectionMode;
    }
    if (applyScalars && option?.applyFieldOfView && Number.isFinite(worldPose?.fieldOfView)) {
        projection.fieldOfViewRadians = worldPose.fieldOfView;
    }
    if (
        applyScalars &&
        option?.applyOrthographicSize &&
        Number.isFinite(worldPose?.orthographicSize)
    ) {
        projection.orthographicSize = worldPose.orthographicSize;
    }

    if (Object.keys(projection).length === 0) {
        return false;
    }

    if (typeof state?.applyUserCameraProjection === "function") {
        state.applyUserCameraProjection(projection);
        return true;
    }

    const camera = state?.userCamera;
    if (
        camera?.isPerspectiveCamera &&
        Number.isFinite(projection.fieldOfViewRadians)
    ) {
        camera.fov = (projection.fieldOfViewRadians * 180) / Math.PI;
        camera.updateProjectionMatrix();
        return true;
    }

    return false;
}

function applyCameraPoseImmediate(state, entry, option, worldPose) {
    applyCameraProjectionFromOption(state, option, worldPose);

    const camera = state?.userCamera;
    const cameraState = state?.cameraState;
    if (!camera || !cameraState) {
        return;
    }

    const currentPosition = camera.position.toArray();
    const currentRotation = camera.quaternion.toArray();
    const targetPosition = option.applyPosition && worldPose.position
        ? Array.from(worldPose.position)
        : currentPosition;
    const targetRotation = option.applyRotation && worldPose.rotation
        ? Array.from(worldPose.rotation)
        : currentRotation;

    setCameraTransform(camera, cameraState, targetPosition, targetRotation);

    entry.appliedOptionIndex = option.index;
}

function setCameraTransform(camera, cameraState, position, rotation) {
    if (cameraState.refocusAfterState) {
        cameraState.needsOrbitFocus = true;
        cameraState.orbitOrientationOffset = null;
    }
    const positionVec = new THREE.Vector3(position[0], position[1], position[2]);
    const rotationQuat = new THREE.Quaternion(
        rotation[0],
        rotation[1],
        rotation[2],
        rotation[3]
    ).normalize();
    const distance = Number.isFinite(cameraState.distance) ? cameraState.distance : 1;
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(rotationQuat);
    const target = positionVec.clone().add(forward.multiplyScalar(distance));
    cameraState.target.copy(target);
    cameraState.distance = Math.max(distance, 0.1);
    const offset = positionVec.clone().sub(cameraState.target);
    const len = Math.max(offset.length(), 0.0001);
    cameraState.theta = Math.atan2(offset.x, offset.z);
    cameraState.phi = Math.acos(THREE.MathUtils.clamp(offset.y / len, -1, 1));
    camera.position.copy(positionVec);
    camera.quaternion.copy(rotationQuat);
    camera.updateMatrixWorld();
}

function scheduleCameraAnimation(state, entry, option, worldPose, duration, easeMode) {
    ensureAnimationState(state);
    applyCameraProjectionFromOption(state, option, worldPose, { applyScalars: false });

    const camera = state?.userCamera;
    const cameraState = state?.cameraState;
    if (!camera || !cameraState) {
        return;
    }

    const fromPosition = camera.position.toArray();
    const fromRotation = camera.quaternion.toArray();
    const fromFov = Number.isFinite(camera.fov) ? (camera.fov * Math.PI) / 180 : null;
    const fromOrthographicSize = Number.isFinite(cameraState.orthographicSize)
        ? cameraState.orthographicSize
        : null;

    const toPosition = option.applyPosition && worldPose.position
        ? Array.from(worldPose.position)
        : Array.from(fromPosition);
    const toRotation = option.applyRotation && worldPose.rotation
        ? Array.from(worldPose.rotation)
        : Array.from(fromRotation);
    const toFov = option.applyFieldOfView && Number.isFinite(worldPose.fieldOfView)
        ? worldPose.fieldOfView
        : fromFov;
    const toOrthographicSize =
        option.applyOrthographicSize && Number.isFinite(worldPose.orthographicSize)
            ? worldPose.orthographicSize
            : fromOrthographicSize;

    state.innoWorksActiveAnimations.push({
        type: "camera",
        entry,
        option,
        duration: Math.max(0.0001, duration),
        elapsed: 0,
        easeMode: easeMode ?? "Linear",
        applyPosition: option.applyPosition,
        applyRotation: option.applyRotation,
        applyFieldOfView: option.applyFieldOfView,
        applyOrthographicSize: option.applyOrthographicSize,
        fromPosition,
        toPosition,
        fromRotation,
        toRotation,
        fromFov,
        toFov,
        fromOrthographicSize,
        toOrthographicSize
    });
}

function applyGroupOption(state, entry, requestedIndex, options = {}) {
    if (!entry || !Array.isArray(entry.options) || entry.options.length === 0) {
        return entry?.currentOption ?? 0;
    }

    const { forceImmediate = false, suppressUiUpdate = false, visitedGroups = new Set() } = options ?? {};
    if (visitedGroups.has(entry.id)) return entry.currentOption;
    const nextVisitedGroups = new Set(visitedGroups).add(entry.id);

    const parsedIndex = Number(requestedIndex);
    const numericIndex = Number.isNaN(parsedIndex)
        ? Number(entry.currentOption ?? entry.options[0].index)
        : parsedIndex;

    const targetOption =
        entry.options.find((option) => option.index === numericIndex) ??
        entry.options.find((option) => option.index === entry.currentOption) ??
        entry.options[0];

    if (!targetOption) {
        return entry.currentOption ?? 0;
    }

    const duration = forceImmediate
        ? 0
        : Math.max(0, targetOption.transitionDuration ?? 0);

    const useParentTiming = String(entry.transitionTimingMode).toLowerCase() !== "linkedstatedurations";
    const executeTargets = () => {
        if (!Array.isArray(targetOption.targets)) {
            return;
        }

        targetOption.targets.forEach((target) => {
            const targetEntry = findRuntimeEntryByTarget(state, target);
            if (!targetEntry || targetEntry === entry || typeof targetEntry.applyOption !== "function") {
                return;
            }

            targetEntry.applyOption(target.optionIndex, {
                visitedGroups: nextVisitedGroups,
                forceImmediate,
                suppressUiUpdate,
                durationOverride: useParentTiming && !forceImmediate ? duration : null,
                easeModeOverride: useParentTiming ? targetOption.easeMode : null
            });
        });
    };

    removeInnoWorksAnimation(state, entry.id, "group");
    executeTargets();
    entry.appliedOptionIndex = targetOption.index;

    entry.currentOption = targetOption.index;
    updateUiSelection(state, entry.id, entry.currentOption, suppressUiUpdate);
    return entry.currentOption;
}

function vectorFromData(data) {
    if (!data) {
        return null;
    }

    return [data.X ?? 0, data.Y ?? 0, data.Z ?? 0];
}

function quaternionFromData(data) {
    if (!data) {
        return null;
    }

    const x = data.X ?? 0;
    const y = data.Y ?? 0;
    const z = data.Z ?? 0;
    const w = data.W ?? 1;
    return [x, y, z, w];
}

function unityVectorToGltf(vec) {
    if (!Array.isArray(vec)) {
        return vec;
    }
    return [-vec[0], vec[1], vec[2]];
}

function unityQuaternionToGltf(quat) {
    if (!Array.isArray(quat) || quat.length !== 4) {
        return Array.isArray(quat) ? [...quat] : [0, 0, 0, 1];
    }
    return [quat[0], -quat[1], -quat[2], quat[3]];
}

function buildParentIndexLookup(gltf) {
    const lookup = new Map();
    if (!gltf || !Array.isArray(gltf.nodes)) {
        return lookup;
    }

    gltf.nodes.forEach((node, index) => {
        if (!node || !Array.isArray(node.children)) {
            return;
        }
        node.children.forEach((childIndex) => {
            if (Number.isInteger(childIndex)) {
                lookup.set(childIndex, index);
            }
        });
    });

    return lookup;
}


function ensureAnimationState(state) {
    if (!state) {
        return;
    }
    if (!Array.isArray(state.innoWorksActiveAnimations)) {
        state.innoWorksActiveAnimations = [];
    }
}

function removeInnoWorksAnimation(state, entryId, type) {
    ensureAnimationState(state);
    if (!state || !Array.isArray(state.innoWorksActiveAnimations)) {
        return;
    }

    for (let i = state.innoWorksActiveAnimations.length - 1; i >= 0; i--) {
        const animation = state.innoWorksActiveAnimations[i];
        if (!animation || !animation.entry) {
            continue;
        }

        if (
            animation.entry.id === entryId &&
            animation.type === type
        ) {
            (animation.transitions ?? []).forEach((transition) => transition.workingMaterial?.dispose?.());
            state.innoWorksActiveAnimations.splice(i, 1);
        }
    }
}

function scheduleLegacyAnimation(state, entry, clip, option) {
    ensureAnimationState(state);
    if (!state || !entry || !clip) {
        return;
    }

    state.innoWorksActiveAnimations.push({
        type: "legacy-animation",
        entry,
        clip,
        option,
        time: 0,
        speed: typeof option?.speed === "number" ? option.speed : 1,
        loop: option?.loop !== undefined ? !!option.loop : !!clip.loop,
        wrapMode: clip.wrapMode ?? "Default"
    });
}

function scheduleMaterialAnimation(state, entry, fromOption, toOption, duration, easeMode) {
    ensureAnimationState(state);
    if (!state || !entry) {
        return;
    }

    const targetMaterial = toOption?.material ?? resolveMaterialByIndex(state, toOption?.materialIndex);
    const transitions = [];
    (entry.rendererBindings ?? []).forEach((binding) => {
        (binding?.assignments ?? []).forEach((assignment) => {
            const { mesh, index } = assignment;
            const sourceMaterial = index === null ? mesh.material : mesh.material[index];
            if (!mesh || !sourceMaterial || !targetMaterial || typeof sourceMaterial.clone !== "function") {
                return;
            }
            const workingMaterial = targetMaterial.isMeshPhysicalMaterial && !sourceMaterial.isMeshPhysicalMaterial
                ? upgradeModelVistaPhysicalMaterial(sourceMaterial) : sourceMaterial.clone();
            assignMaterial(assignment, workingMaterial);
            transitions.push({ mesh, sourceMaterial, targetMaterial, workingMaterial });
        });
    });

    const animation = {
        type: "material",
        entry,
        fromOption,
        toOption,
        duration: Math.max(0.0001, duration),
        elapsed: 0,
        easeMode: easeMode ?? "Linear",
        transitions
    };

    state.innoWorksActiveAnimations.push(animation);
}

function scheduleTransformAnimation(state, entry, option, duration, easeMode) {
    ensureAnimationState(state);
    if (!state || !entry) {
        return;
    }
    const object =
        entry.nodeObject ??
        state.nodeIndexToObject?.get(entry.nodeIndex) ??
        null;
    const fromTranslation = object
        ? [object.position.x, object.position.y, object.position.z]
        : [...entry.defaultTranslation];
    const fromRotation = object
        ? [object.quaternion.x, object.quaternion.y, object.quaternion.z, object.quaternion.w]
        : [...entry.defaultRotation];

    const toTranslation = option.applyPosition && Array.isArray(option.translation)
        ? [...option.translation]
        : [...entry.defaultTranslation];
    const toRotation = option.applyRotation && Array.isArray(option.rotation)
        ? [...option.rotation]
        : [...entry.defaultRotation];

    const animation = {
        type: "transform",
        entry,
        option,
        duration: Math.max(0.0001, duration),
        elapsed: 0,
        easeMode: easeMode ?? "Linear",
        fromTranslation,
        toTranslation,
        fromRotation: normalizeQuaternion(fromRotation),
        toRotation: normalizeQuaternion(toRotation)
    };

    state.innoWorksActiveAnimations.push(animation);
}

function invalidateRendererScene(state) {
    // No-op for three.js renderer.
}

function setActivationNodes(state, entry, activeSet) {
    if (!entry || !entry.nodeStates) {
        return false;
    }

    let changed = false;
    entry.nodeStates.forEach((stateInfo, nodeIndex) => {
        const object = stateInfo?.object ?? null;
        if (!object) {
            return;
        }
        const indexKey = stateInfo?.nodeIndex ?? nodeIndex;
        const shouldBeActive = activeSet.has(indexKey);
        if (object.visible !== shouldBeActive) {
            object.visible = shouldBeActive;
            changed = true;
        }
    });

    return changed;
}

function updateUiSelection(targetState, entryId, optionIndex, suppress = false) {
    if (suppress) {
        return;
    }
    if (!targetState?.isSelected) {
        return;
    }

    const uiModel = targetState?.innoWorksUiModel;
    if (!uiModel || typeof uiModel.updateConfigurableSelection !== "function") {
        return;
    }

    uiModel.updateConfigurableSelection(entryId, optionIndex);
    if (targetState.selectedConfigurableOptions) {
        targetState.selectedConfigurableOptions = {
            ...targetState.selectedConfigurableOptions,
            [entryId]: optionIndex
        };
    }
}

function findRuntimeEntryByKey(state, key) {
    if (!state || !(state.innoWorksRuntimeByPath instanceof Map)) {
        return null;
    }

    return state.innoWorksRuntimeByPath.get(key) ?? null;
}

function findRuntimeEntryByTarget(state, target) {
    if (!target) {
        return null;
    }

    const hasIndex = Number.isInteger(target.containerIndex) && target.containerIndex >= 0;
    if (hasIndex) {
        const indexedKey = makeRuntimePathKey(target.containerPath, target.containerType, target.containerIndex);
        const indexedEntry = findRuntimeEntryByKey(state, indexedKey);
        if (indexedEntry) {
            return indexedEntry;
        }
    }

    const fallbackKey = makeRuntimePathKey(target.containerPath, target.containerType);
    return findRuntimeEntryByKey(state, fallbackKey);
}

function ensureInnoWorksInputState(state) {
    if (!state) {
        return;
    }
    if (!(state.innoWorksActiveInputHolds instanceof Map)) {
        state.innoWorksActiveInputHolds = new Map();
    }
    if (!(state.innoWorksGamepadControlStates instanceof Map)) {
        state.innoWorksGamepadControlStates = new Map();
    }
}

function releaseInnoWorksInputState(state, suppressUiUpdate = false) {
    if (!state) {
        return false;
    }

    ensureInnoWorksInputState(state);

    let changed = false;
    state.innoWorksActiveInputHolds.forEach((hold) => {
        const entry = state.innoWorksRuntime?.get(hold.entryId);
        if (
            !entry ||
            typeof entry.applyOption !== "function" ||
            entry.currentOption !== hold.optionIndex
        ) {
            return;
        }

        entry.applyOption(hold.returnOptionIndex, { suppressUiUpdate });
        changed = true;
    });

    state.innoWorksActiveInputHolds.clear();
    state.innoWorksGamepadControlStates.clear();
    return changed;
}

function handleInnoWorksKeyboardInput(state, event, pressed) {
    if (!shouldHandleInnoWorksKeyboardEvent(event)) {
        return false;
    }

    const control = getKeyboardEventControl(event);
    if (!control) {
        return false;
    }

    return handleInnoWorksInputControl(state, control, pressed);
}

function shouldHandleInnoWorksKeyboardEvent(event) {
    if (!event) {
        return false;
    }

    const key = normalizeKeyboardEventKey(event.key);
    const isModifierKey = key === "control" || key === "alt" || key === "meta";
    return !(event.ctrlKey || event.altKey || event.metaKey) || isModifierKey;
}

function normalizeKeyboardEventKey(key) {
    if (typeof key !== "string" || key.length === 0) {
        return "";
    }
    if (key === " ") {
        return " ";
    }
    return key.toLowerCase();
}

function getKeyboardEventControl(event) {
    if (!event) {
        return null;
    }

    const controlKeys = new Set();
    if (typeof event.code === "string" && event.code.length > 0) {
        controlKeys.add(`keyboard:code:${event.code}`);
    }

    const key = normalizeKeyboardEventKey(event.key);
    if (key.length > 0) {
        controlKeys.add(`keyboard:key:${key}`);
    }

    if (controlKeys.size === 0) {
        return null;
    }

    const keys = [...controlKeys];
    return {
        type: "keyboard",
        controlKey: keys[0],
        controlKeys: keys
    };
}

function updateInnoWorksGamepadInput(state) {
    if (
        !state ||
        !(state.innoWorksRuntime instanceof Map) ||
        typeof navigator === "undefined" ||
        typeof navigator.getGamepads !== "function"
    ) {
        return false;
    }

    ensureInnoWorksInputState(state);

    const previousControls = state.innoWorksGamepadControlStates;
    const nextControls = new Map();
    collectActiveGamepadControls().forEach((control) => {
        if (control?.controlKey) {
            nextControls.set(control.controlKey, control);
        }
    });

    let handled = false;
    previousControls.forEach((control, key) => {
        if (!nextControls.has(key)) {
            handled = handleInnoWorksInputControl(state, control, false) || handled;
        }
    });
    nextControls.forEach((control, key) => {
        if (!previousControls.has(key)) {
            handled = handleInnoWorksInputControl(state, control, true) || handled;
        }
    });

    state.innoWorksGamepadControlStates = nextControls;
    return handled;
}

function collectActiveGamepadControls() {
    if (typeof navigator === "undefined" || typeof navigator.getGamepads !== "function") {
        return [];
    }

    const gamepads = Array.from(navigator.getGamepads() ?? []).filter(Boolean);
    const controls = [];
    gamepads.forEach((gamepad) => {
        collectActiveGamepadButtonControls(gamepad, controls);
        collectActiveGamepadAxisControls(gamepad, controls);
    });
    return controls;
}

function collectActiveGamepadButtonControls(gamepad, controls) {
    if (!gamepad || !Array.isArray(gamepad.buttons)) {
        return;
    }

    gamepad.buttons.forEach((button, index) => {
        const value = typeof button?.value === "number" ? button.value : 0;
        if (button?.pressed !== true && value <= GAMEPAD_AXIS_TRIGGER_THRESHOLD) {
            return;
        }

        const controlKey = `gamepad:button:${index}`;
        controls.push({
            type: "gamepad-button",
            controlKey,
            controlKeys: [controlKey]
        });
    });
}

function collectActiveGamepadAxisControls(gamepad, controls) {
    if (!gamepad || !Array.isArray(gamepad.axes)) {
        return;
    }

    gamepad.axes.forEach((value, axisIndex) => {
        if (!Number.isFinite(value) || Math.abs(value) <= GAMEPAD_AXIS_TRIGGER_THRESHOLD) {
            return;
        }

        const direction = value > 0 ? "positive" : "negative";
        const directionKey = `gamepad:axis:${axisIndex}:${direction}`;
        controls.push({
            type: "gamepad-axis",
            controlKey: directionKey,
            controlKeys: [directionKey]
        });

        const anyKey = `gamepad:axis:${axisIndex}:any`;
        controls.push({
            type: "gamepad-axis",
            controlKey: anyKey,
            controlKeys: [anyKey]
        });
    });
}

function handleInnoWorksInputControl(state, control, pressed) {
    if (!state || !(state.innoWorksRuntime instanceof Map) || !control) {
        return false;
    }

    ensureInnoWorksInputState(state);

    let handled = false;
    state.innoWorksRuntime.forEach((entry) => {
        if (
            !entry ||
            typeof entry.applyOption !== "function" ||
            !Array.isArray(entry.options) ||
            entry.options.length === 0
        ) {
            return;
        }

        if (pressed) {
            handled = handleInnoWorksInputPress(state, entry, control) || handled;
        } else {
            handled = handleInnoWorksInputRelease(state, entry, control) || handled;
        }
    });

    return handled;
}

function handleInnoWorksInputPress(state, entry, control) {
    let handled = false;

    if (inputActionMatchesControl(entry.previousInputAction, control)) {
        const optionIndex = getSteppedInnoWorksOptionIndex(entry, -1);
        if (optionIndex !== null) {
            entry.applyOption(optionIndex);
            handled = true;
        }
    }

    if (inputActionMatchesControl(entry.nextInputAction, control)) {
        const optionIndex = getSteppedInnoWorksOptionIndex(entry, 1);
        if (optionIndex !== null) {
            entry.applyOption(optionIndex);
            handled = true;
        }
    }

    entry.options.forEach((option) => {
        if (inputActionMatchesControl(option.triggerInputAction, control)) {
            entry.applyOption(option.index);
            handled = true;
        }

        if (inputActionMatchesControl(option.holdInputAction, control)) {
            const holdKey = makeInnoWorksInputHoldKey(entry, option, control);
            if (!state.innoWorksActiveInputHolds.has(holdKey)) {
                state.innoWorksActiveInputHolds.set(holdKey, {
                    entryId: entry.id,
                    optionIndex: option.index,
                    returnOptionIndex: entry.currentOption
                });
            }
            entry.applyOption(option.index);
            handled = true;
        }
    });

    return handled;
}

function handleInnoWorksInputRelease(state, entry, control) {
    let handled = false;

    entry.options.forEach((option) => {
        if (!inputActionMatchesControl(option.holdInputAction, control)) {
            return;
        }

        const holdKey = makeInnoWorksInputHoldKey(entry, option, control);
        const hold = state.innoWorksActiveInputHolds.get(holdKey);
        if (!hold) {
            return;
        }

        state.innoWorksActiveInputHolds.delete(holdKey);
        if (entry.currentOption === hold.optionIndex) {
            entry.applyOption(hold.returnOptionIndex);
        }
        handled = true;
    });

    return handled;
}

function inputActionMatchesControl(action, control) {
    if (!action || !Array.isArray(action.bindings) || !control) {
        return false;
    }

    const controlKeys = new Set(
        Array.isArray(control.controlKeys)
            ? control.controlKeys
            : [control.controlKey]
    );
    if (controlKeys.size === 0) {
        return false;
    }

    return action.bindings.some((binding) => {
        if (!Array.isArray(binding?.controlKeys)) {
            return false;
        }
        return binding.controlKeys.some((key) => controlKeys.has(key));
    });
}

function getSteppedInnoWorksOptionIndex(entry, direction) {
    if (!entry || !Array.isArray(entry.options) || entry.options.length === 0) {
        return null;
    }

    const currentPosition = entry.options.findIndex((option) =>
        option.index === entry.currentOption
    );
    const safePosition = currentPosition >= 0 ? currentPosition : 0;
    const step = Number.isFinite(direction) ? direction : 0;
    const nextPosition =
        (safePosition + step + entry.options.length) % entry.options.length;
    return entry.options[nextPosition]?.index ?? null;
}

function makeInnoWorksInputHoldKey(entry, option, control) {
    const controlKey = control?.controlKey ??
        (Array.isArray(control?.controlKeys) ? control.controlKeys[0] : "");
    return `${entry?.id ?? ""}|${option?.index ?? ""}|${controlKey}`;
}

function hasActiveInnoWorksTransition(state, entryId) {
    if (!Array.isArray(state?.innoWorksActiveAnimations) || entryId === undefined || entryId === null) {
        return false;
    }

    const transitionTypes = new Set(["material", "activation", "transform", "camera", "group"]);
    return state.innoWorksActiveAnimations.some((animation) =>
        animation?.entry?.id === entryId && transitionTypes.has(animation.type)
    );
}

function updateInnoWorksAutoPlay(state, deltaSeconds) {
    if (!state || !(state.innoWorksRuntime instanceof Map)) {
        return false;
    }

    const dt = Number.isFinite(deltaSeconds) ? Math.max(0, deltaSeconds) : 0;
    if (dt <= 0) {
        return false;
    }

    let changed = false;
    state.innoWorksRuntime.forEach((entry) => {
        const autoPlay = entry?.autoPlay;
        if (
            entry?.authoringDeferred ||
            !autoPlay?.enabled ||
            typeof entry.applyOption !== "function" ||
            !Array.isArray(entry.options) ||
            entry.options.length < 2
        ) {
            return;
        }

        if (hasActiveInnoWorksTransition(state, entry.id)) {
            return;
        }

        let stepCount = 1;
        if (autoPlay.interval > 0) {
            autoPlay.elapsed = (autoPlay.elapsed ?? 0) + dt;
            if (autoPlay.elapsed < autoPlay.interval) {
                return;
            }

            stepCount = Math.max(1, Math.floor(autoPlay.elapsed / autoPlay.interval));
            autoPlay.elapsed %= autoPlay.interval;
        } else {
            autoPlay.elapsed = 0;
        }

        let targetOption = null;
        if (autoPlay.random) {
            const candidates = entry.options.filter((option) => option.index !== entry.currentOption);
            targetOption = candidates[Math.floor(Math.random() * candidates.length)] ?? entry.options[0];
        } else {
            const currentIndex = entry.options.findIndex((option) => option.index === entry.currentOption);
            const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
            targetOption = entry.options[(safeCurrentIndex + stepCount) % entry.options.length];
        }

        if (!targetOption) {
            return;
        }

        entry.applyOption(targetOption.index);
        changed = true;
    });

    return changed;
}

function updateInnoWorksAnimations(state, deltaSeconds) {
    if (!state || !Array.isArray(state.innoWorksActiveAnimations) || state.innoWorksActiveAnimations.length === 0) {
        return false;
    }

    const animations = state.innoWorksActiveAnimations;
    const gltf = state.gltf;

    const dt = Number.isFinite(deltaSeconds) ? Math.max(0, deltaSeconds) : 0;
    let changed = false;
    let needsHierarchyRefresh = false;
    let needsExtentRefresh = false;

    for (let i = animations.length - 1; i >= 0; i--) {
        const animation = animations[i];
        if (!animation || !animation.entry) {
            animations.splice(i, 1);
            continue;
        }

        animation.elapsed += dt;
        const progress = animation.duration > 0
            ? Math.min(1, animation.elapsed / animation.duration)
            : 1;
        const eased = evaluateEase(animation.easeMode, progress);

        if (animation.type === "transform") {
            const entry = animation.entry;
            const object =
                entry.nodeObject ??
                state?.nodeIndexToObject?.get(entry.nodeIndex) ??
                null;
            if (!object) {
                animations.splice(i, 1);
                continue;
            }

            if (animation.option.applyPosition) {
                const next = lerpVector(animation.fromTranslation, animation.toTranslation, eased);
                object.position.set(next[0], next[1], next[2]);
                changed = true;
                needsExtentRefresh = true;
            }

            if (animation.option.applyRotation) {
                const next = slerp(animation.fromRotation, animation.toRotation, eased);
                object.quaternion.set(next[0], next[1], next[2], next[3]);
                changed = true;
                needsExtentRefresh = true;
            }
            object.updateMatrixWorld(true);

            if (progress >= 1) {
                if (animation.option.applyPosition) {
                    const next = animation.toTranslation;
                    object.position.set(next[0], next[1], next[2]);
                } else {
                    const fallback = entry.defaultTranslation;
                    object.position.set(fallback[0], fallback[1], fallback[2]);
                }

                if (animation.option.applyRotation) {
                    const next = normalizeQuaternion(animation.toRotation);
                    object.quaternion.set(next[0], next[1], next[2], next[3]);
                } else {
                    const fallback = entry.defaultRotation;
                    object.quaternion.set(fallback[0], fallback[1], fallback[2], fallback[3]);
                }

                object.updateMatrixWorld(true);
                entry.appliedOptionIndex = animation.option.index;
                animations.splice(i, 1);
                changed = true;
                needsExtentRefresh = true;
                continue;
            }
        } else if (animation.type === "camera") {
            const camera = state.userCamera;
            const cameraState = state.cameraState;
            if (!camera || !cameraState) {
                animations.splice(i, 1);
                continue;
            }

            const position = animation.applyPosition
                ? lerpVector(animation.fromPosition, animation.toPosition, eased)
                : animation.fromPosition;
            const rotation = animation.applyRotation
                ? slerp(animation.fromRotation, animation.toRotation, eased)
                : animation.fromRotation;

            setCameraTransform(camera, cameraState, position, rotation);

            if (
                animation.applyFieldOfView &&
                camera.isPerspectiveCamera &&
                Number.isFinite(animation.fromFov) &&
                Number.isFinite(animation.toFov)
            ) {
                const fov = lerp(animation.fromFov, animation.toFov, eased);
                if (typeof state.applyUserCameraProjection === "function") {
                    state.applyUserCameraProjection({ fieldOfViewRadians: fov });
                } else {
                    camera.fov = (fov * 180) / Math.PI;
                    camera.updateProjectionMatrix();
                }
            }
            if (
                animation.applyOrthographicSize &&
                camera.isOrthographicCamera &&
                Number.isFinite(animation.fromOrthographicSize) &&
                Number.isFinite(animation.toOrthographicSize)
            ) {
                const orthographicSize = lerp(
                    animation.fromOrthographicSize,
                    animation.toOrthographicSize,
                    eased
                );
                state.applyUserCameraProjection?.({ orthographicSize });
            }

            changed = true;

            if (progress >= 1) {
                animation.entry.appliedOptionIndex = animation.option.index;
                updateUiSelection(state, animation.entry.id, animation.option.index);
                animations.splice(i, 1);
                continue;
            }
        } else if (animation.type === "material") {
            const entry = animation.entry;
            (animation.transitions ?? []).forEach((transition) => {
                interpolateThreeMaterial(
                    transition.workingMaterial,
                    transition.sourceMaterial,
                    transition.targetMaterial,
                    eased
                );
            });
            changed = true;
            if (progress >= 1) {
                applyMaterialOptionImmediate(state, entry, animation.toOption);
                entry.appliedOptionIndex = animation.toOption.index;
                (animation.transitions ?? []).forEach((transition) => transition.workingMaterial?.dispose?.());
                animations.splice(i, 1);
                continue;
            }
        } else if (animation.type === "shader") {
            animation.entry.targetMaterials.forEach((material, materialIndex) => {
                const value = interpolateModelVistaValue(
                    animation.fromValues[materialIndex],
                    animation.option.value,
                    eased
                );
                applyShaderMaterialValue(material, animation.entry, value);
            });
            changed = true;
            if (progress >= 1) {
                animation.entry.appliedOptionIndex = animation.option.index;
                animations.splice(i, 1);
                continue;
            }
        } else if (animation.type === "light") {
            animation.entry.targetLights.forEach((light, lightIndex) => {
                const from = animation.fromValues[lightIndex];
                applyLightStateValue(
                    light,
                    THREE.MathUtils.lerp(from.ev100, animation.option.ev100, eased),
                    THREE.MathUtils.lerp(from.temperature, animation.option.temperature, eased),
                    interpolateModelVistaValue(from.filter, animation.option.filter, eased)
                );
            });
            changed = true;
            if (progress >= 1) {
                animation.entry.appliedOptionIndex = animation.option.index;
                animations.splice(i, 1);
                continue;
            }
        } else if (animation.type === "activation") {
            const entry = animation.entry;
            if (!animation.switched && (progress >= 1 || eased >= 0.5)) {
                const changedState = setActivationNodes(state, entry, animation.toSet ?? new Set());
                entry.appliedOptionIndex = animation.toOptionIndex;
                updateUiSelection(state, entry.id, animation.toOptionIndex);
                animation.switched = true;
                changed = changed || changedState;
                if (changedState) {
                    needsExtentRefresh = true;
                }
            }

            if (progress >= 1) {
                animations.splice(i, 1);
                continue;
            }
        } else if (animation.type === "group") {
            if (!animation.executed && (progress >= 1 || eased >= 0.5)) {
                try {
                    animation.action?.();
                } catch (groupError) {
                    console.warn("InnoWorks: Failed to execute group animation.", groupError);
                }
                animation.executed = true;
                animation.entry.appliedOptionIndex = animation.entry.currentOption;
                updateUiSelection(state, animation.entry.id, animation.entry.currentOption);
                changed = true;
            }

            if (progress >= 1) {
                animations.splice(i, 1);
                continue;
            }
        } else if (animation.type === "legacy-animation") {
            if (state.animationPlaybackPaused) continue;
            const entry = animation.entry;
            const clip = animation.clip;
            if (!clip || !Array.isArray(clip.channels) || clip.channels.length === 0) {
                animations.splice(i, 1);
                continue;
            }

            const loops = animation.loop ?? clip.loop ?? false;
            animation.loop = loops;

            const speed = Number.isFinite(animation.speed) ? animation.speed : 1;
            animation.time = (animation.time ?? 0) + dt * speed;

            const clipDuration = Number.isFinite(clip.duration) ? Math.max(0, clip.duration) : 0;
            let localTime = animation.time;
            let finished = false;

            if (clipDuration > 0) {
                if (loops) {
                    const span = clipDuration > 0 ? clipDuration : 1;
                    localTime = ((localTime % span) + span) % span;
                } else if (localTime >= clipDuration) {
                    localTime = clipDuration;
                    finished = true;
                } else if (localTime < 0) {
                    localTime = 0;
                }
            } else {
                finished = !loops;
                localTime = 0;
            }

            animation.currentTime = localTime;

            const nodeUpdates = new Map();

            clip.channels.forEach((channel) => {
                if (!channel) {
                    return;
                }

                const nodeIndex = channel.nodeIndex;
                if (!Number.isInteger(nodeIndex) || nodeIndex < 0 || nodeIndex >= gltf.nodes.length) {
                    return;
                }

                const node = gltf.nodes[nodeIndex];
                if (!node) {
                    return;
                }

                let info = nodeUpdates.get(nodeIndex);
                if (!info) {
                    info = {
                        node,
                        nodeIndex,
                        translation: Array.isArray(node.translation) ? [...node.translation] : [0, 0, 0],
                        rotation: Array.isArray(node.rotation) ? [...node.rotation] : [0, 0, 0, 1],
                        scale: Array.isArray(node.scale) ? [...node.scale] : [1, 1, 1],
                        euler: [0, 0, 0],
                        updatedTranslation: false,
                        updatedRotation: false,
                        updatedScale: false,
                        updatedRotationEuler: false
                    };
                    nodeUpdates.set(nodeIndex, info);
                }

                const value = evaluateLegacyCurve(
                    channel.curve,
                    localTime,
                    loops,
                    clipDuration,
                    animation.wrapMode
                );

                if (channel.property === "translation" && channel.axis !== null && channel.axis <= 2) {
                    info.translation[channel.axis] = value;
                    info.updatedTranslation = true;
                } else if (channel.property === "scale" && channel.axis !== null && channel.axis <= 2) {
                    info.scale[channel.axis] = value;
                    info.updatedScale = true;
                } else if (channel.property === "rotation" && channel.axis !== null && channel.axis <= 3) {
                    info.rotation[channel.axis] = value;
                    info.updatedRotation = true;
                } else if (channel.property === "rotationEuler" && channel.axis !== null && channel.axis <= 2) {
                    info.euler[channel.axis] = value;
                    info.updatedRotationEuler = true;
                }
            });

            if (nodeUpdates.size > 0) {
                nodeUpdates.forEach((info) => {
                    const node = info.node;
                    if (!node) {
                        return;
                    }

                    if (node.matrix) {
                        delete node.matrix;
                    }

                    if (info.updatedTranslation) {
                        node.translation = [...info.translation];
                        changed = true;
                        needsHierarchyRefresh = true;
                    }

                    if (info.updatedScale) {
                        node.scale = [...info.scale];
                        changed = true;
                        needsHierarchyRefresh = true;
                    }

                    if (info.updatedRotation) {
                        node.rotation = normalizeQuaternion(info.rotation);
                        changed = true;
                        needsHierarchyRefresh = true;
                    }

                    if (info.updatedRotationEuler) {
                        node.rotation = quaternionFromEulerDegrees(info.euler);
                        changed = true;
                        needsHierarchyRefresh = true;
                    }

                    const object = state?.nodeIndexToObject?.get(info.nodeIndex) ?? null;
                    if (object) {
                        if (info.updatedTranslation) {
                            object.position.set(
                                info.translation[0],
                                info.translation[1],
                                info.translation[2]
                            );
                        }
                        if (info.updatedScale) {
                            object.scale.set(
                                info.scale[0],
                                info.scale[1],
                                info.scale[2]
                            );
                        }
                        if (info.updatedRotationEuler) {
                            const q = quaternionFromEulerDegrees(info.euler);
                            object.quaternion.set(q[0], q[1], q[2], q[3]);
                        } else if (info.updatedRotation) {
                            const q = normalizeQuaternion(info.rotation);
                            object.quaternion.set(q[0], q[1], q[2], q[3]);
                        }
                        object.updateMatrixWorld(true);
                    }
                });

                entry.appliedOptionIndex = animation.option?.index ?? entry.currentOption;
            }
            else {
                entry.appliedOptionIndex = animation.option?.index ?? entry.currentOption;
            }

            if (finished) {
                animations.splice(i, 1);
                continue;
            }
        }
    }

    if (needsExtentRefresh) {
        refreshSharedSceneExtents(state);
    }

    return changed;
}

function resolveMaterialByIndex(state, materialIndex) {
    if (!state || materialIndex === undefined || materialIndex === null) {
        return null;
    }
    if (state.materialsByIndex?.has(materialIndex)) {
        return state.materialsByIndex.get(materialIndex);
    }
    const materialName = state.gltf?.materials?.[materialIndex]?.name;
    if (materialName && state.materialsByName?.has(materialName)) {
        return state.materialsByName.get(materialName);
    }
    return null;
}

function interpolateThreeMaterial(output, from, to, t) {
    if (!output || !from || !to) {
        return;
    }
    const amount = THREE.MathUtils.clamp(t, 0, 1);
    if (output.color && from.color && to.color) {
        output.color.copy(from.color).lerp(to.color, amount);
    }
    if (output.emissive && from.emissive && to.emissive) {
        output.emissive.copy(from.emissive).lerp(to.emissive, amount);
    }
    ["opacity", "roughness", "metalness", "emissiveIntensity", "aoMapIntensity", "transmission", "clearcoat", "clearcoatRoughness"].forEach((key) => {
        const start = from[key] ?? (key.startsWith("clearcoat") ? 0 : undefined), end = to[key] ?? (key.startsWith("clearcoat") ? 0 : undefined);
        if (Number.isFinite(start) && Number.isFinite(end)) {
            output[key] = THREE.MathUtils.lerp(start, end, amount);
        }
    });
    if (amount >= 0.5) {
        ["map", "normalMap", "aoMap", "metalnessMap", "roughnessMap", "emissiveMap", "clearcoatMap", "clearcoatRoughnessMap", "clearcoatNormalMap"].forEach((key) => {
            output[key] = to[key] ?? null;
        });
        output.transparent = to.transparent;
        output.alphaTest = to.alphaTest;
        output.side = to.side;
    }
    output.needsUpdate = true;
}

function applyMaterialOptionImmediate(state, entry, option) {
    if (!entry || !option) {
        return;
    }

    if (!Array.isArray(entry?.rendererBindings)) {
        entry.appliedOptionIndex = option.index;
        return;
    }

    const targetMaterial = option.material ?? resolveMaterialByIndex(state, option.materialIndex);
    entry.rendererBindings.forEach((binding) => {
        if (!binding || !Array.isArray(binding.meshes)) {
            return;
        }
        (binding.assignments ?? []).forEach((assignment) => {
            const material = targetMaterial ?? assignment.material;
            assignMaterial(assignment, material);
            if (material) material.needsUpdate = true;
        });
    });
    entry.appliedOptionIndex = option.index;
}

function evaluateEase(mode, t) {
    const clamped = Math.min(1, Math.max(0, t));
    switch ((mode ?? "Linear").toLowerCase()) {
    case "easein":
    case "easeinquad":
        return clamped * clamped;
    case "easeout":
    case "easeoutquad":
        return 1 - (1 - clamped) * (1 - clamped);
    case "easeinout":
    case "easeinoutquad":
        return clamped < 0.5
            ? 2 * clamped * clamped
            : 1 - Math.pow(-2 * clamped + 2, 2) / 2;
    default:
        return clamped;
    }
}

function lerp(a, b, t) {
    return a + (b - a) * t;
}

function lerpVector(from, to, t) {
    if (!Array.isArray(from) || !Array.isArray(to)) {
        return Array.isArray(from) ? [...from] : Array.isArray(to) ? [...to] : [0, 0, 0];
    }

    const length = Math.min(from.length, to.length);
    const result = new Array(length);
    for (let i = 0; i < length; i++) {
        result[i] = lerp(from[i], to[i], t);
    }
    return result;
}

function slerp(from, to, t) {
    if (!Array.isArray(from) || !Array.isArray(to) || from.length !== 4 || to.length !== 4) {
        return Array.isArray(to) ? [...to] : [0, 0, 0, 1];
    }

    let dot = from[0] * to[0] + from[1] * to[1] + from[2] * to[2] + from[3] * to[3];
    let toQuat = [...to];
    if (dot < 0) {
        dot = -dot;
        toQuat = toQuat.map((value) => -value);
    }

    if (dot > 0.9995) {
        const result = lerpVector(from, toQuat, t);
        return normalizeQuaternion(result);
    }

    const theta0 = Math.acos(dot);
    const theta = theta0 * t;
    const sinTheta = Math.sin(theta);
    const sinTheta0 = Math.sin(theta0);

    const s0 = Math.cos(theta) - dot * sinTheta / sinTheta0;
    const s1 = sinTheta / sinTheta0;

    return [
        s0 * from[0] + s1 * toQuat[0],
        s0 * from[1] + s1 * toQuat[1],
        s0 * from[2] + s1 * toQuat[2],
        s0 * from[3] + s1 * toQuat[3]
    ];
}

function normalizeQuaternion(quaternion) {
    if (!Array.isArray(quaternion) || quaternion.length !== 4) {
        return [0, 0, 0, 1];
    }

    const length = Math.hypot(quaternion[0], quaternion[1], quaternion[2], quaternion[3]);
    if (length === 0) {
        return [0, 0, 0, 1];
    }

    return quaternion.map((value) => value / length);
}

function quaternionFromEulerDegrees(euler) {
    if (!Array.isArray(euler) || euler.length < 3) {
        return [0, 0, 0, 1];
    }

    const halfToRad = 0.5 * Math.PI / 180;
    const x = (euler[0] ?? 0) * halfToRad;
    const y = (euler[1] ?? 0) * halfToRad;
    const z = (euler[2] ?? 0) * halfToRad;

    const sx = Math.sin(x);
    const cx = Math.cos(x);
    const sy = Math.sin(y);
    const cy = Math.cos(y);
    const sz = Math.sin(z);
    const cz = Math.cos(z);

    const qx = sx * cy * cz - cx * sy * sz;
    const qy = cx * sy * cz + sx * cy * sz;
    const qz = cx * cy * sz - sx * sy * cz;
    const qw = cx * cy * cz + sx * sy * sz;

    return normalizeQuaternion([qx, qy, qz, qw]);
}

function evaluateLegacyCurve(curve, time, loop, duration, wrapMode) {
    if (!curve || !Array.isArray(curve.keyframes) || curve.keyframes.length === 0) {
        return 0;
    }

    if (curve.keyframes.length === 1) {
        return curve.keyframes[0].value ?? 0;
    }

    const start = Number.isFinite(curve.startTime) ? curve.startTime : curve.keyframes[0].time ?? 0;
    const end = Number.isFinite(curve.endTime) ? curve.endTime : curve.keyframes[curve.keyframes.length - 1].time ?? start;
    const length = Math.max(0, end - start);
    let localTime = Number.isFinite(time) ? time : 0;

    const isLooping = loop || normalizeLegacyWrapMode(wrapMode) === "Loop";
    const span = duration > 0 ? duration : length;

    if (isLooping && span > 0) {
        localTime = start + ((localTime - start) % span + span) % span;
    } else {
        if (localTime <= start) {
            return curve.keyframes[0].value ?? 0;
        }
        if (localTime >= end) {
            return curve.keyframes[curve.keyframes.length - 1].value ?? 0;
        }
    }

    let segmentIndex = 0;
    for (let i = 0; i < curve.keyframes.length - 1; i++) {
        if (localTime >= curve.keyframes[i].time && localTime <= curve.keyframes[i + 1].time) {
            segmentIndex = i;
            break;
        }
    }

    const k0 = curve.keyframes[segmentIndex];
    const k1 = curve.keyframes[Math.min(segmentIndex + 1, curve.keyframes.length - 1)];

    if (!k0 || !k1) {
        return k0?.value ?? k1?.value ?? 0;
    }

    if (localTime <= k0.time) {
        return k0.value ?? 0;
    }

    if (localTime >= k1.time) {
        return k1.value ?? 0;
    }

    const dt = k1.time - k0.time;
    if (!Number.isFinite(dt) || dt === 0) {
        return k1.value ?? k0.value ?? 0;
    }

    const t = (localTime - k0.time) / dt;
    const t2 = t * t;
    const t3 = t2 * t;

    const v0 = k0.value ?? 0;
    const v1 = k1.value ?? 0;
    const m0 = (k0.outTangent ?? 0) * dt;
    const m1 = (k1.inTangent ?? 0) * dt;

    return (2 * t3 - 3 * t2 + 1) * v0 +
        (t3 - 2 * t2 + t) * m0 +
        (-2 * t3 + 3 * t2) * v1 +
        (t3 - t2) * m1;
}

function stopModelActions(modelEntry) {
    if (!modelEntry?.actions) {
        return;
    }
    modelEntry.actions.forEach((action) => action.stop());
    modelEntry.activeAnimationIndices = [];
    modelEntry.animationPlaying = false;
}

export {
    stopModelActions,
    normalizeCameraProjectionMode,
    prepareInnoWorksConfigurables,
    ensureRuntimeAnimationStates,
    buildNodeIndexToObjectMap,
    releaseInnoWorksInputState,
    handleInnoWorksKeyboardInput,
    updateInnoWorksGamepadInput,
    updateInnoWorksAutoPlay,
    updateInnoWorksAnimations,
    CAMERA_PROJECTION_MODE,
    UI_VISIBILITY,
    computeCameraWorldPose,
    findRootNodeIndex,
    getContainerRuntimeType,
    resolveNodeByPath,
    findRuntimeEntryByTarget
};
